import { useState, useEffect, useRef, memo, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useVendor } from '../contexts/VendorContext';
import Header from '../components/Header';
import ColumnCustomizer, { Column } from '../components/ColumnCustomizer';
import axios from 'axios';
import toast from 'react-hot-toast';

interface Location {
  id: string;
  name: string;
  type: string;
}

interface Printer {
  id: string;
  name: string;
  location: string;
  type: string;
  status: string;
  serialNumber?: string;
  clientId?: string;
}

interface PrintLayout {
  id: string;
  name: string;
  width: string;
  height: string;
  dpi: string;
  format: string;
}

interface PrintItem {
  sku: string;
  name: string;
  color: string;
  size: string;
  qty: number;
}

// Isolated location search input — manages its own state so typing doesn't re-render the parent
const LocationSearchInput = memo(({ locations, selectedLocationId, onSelect }: {
  locations: any[];
  selectedLocationId: string | null;
  onSelect: (locationId: string) => void;
}) => {
  const [searchFilter, setSearchFilter] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedLoc = selectedLocationId ? locations.find((l: any) => l.id === selectedLocationId) : null;

  const filtered = searchFilter
    ? locations.filter((loc: any) => {
        const locName = (loc.name || '').toLowerCase();
        const locZone = (loc.zone || '').toLowerCase();
        return locName.includes(searchFilter.toLowerCase()) || locZone.includes(searchFilter.toLowerCase());
      })
    : locations;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Select Location <span className="text-xs text-gray-500 dark:text-gray-400">({locations.length} available)</span>
      </label>
      <div className="relative">
        <input
          type="text"
          value={isOpen ? searchFilter : (selectedLoc ? `${selectedLoc.name}${selectedLoc.zone ? ` (Zone: ${selectedLoc.zone})` : ''}` : '')}
          onChange={(e) => {
            setSearchFilter(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchFilter('');
          }}
          placeholder="Type to search locations..."
          className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
        />
        {selectedLoc && !isOpen && (
          <button
            onClick={() => {
              onSelect('');
              setIsOpen(true);
              setSearchFilter('');
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {!selectedLoc && !isOpen && (
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        )}
      </div>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 italic">No matching locations</div>
          ) : (
            filtered.slice(0, 100).map((loc: any) => (
              <button
                key={loc.id}
                onClick={() => {
                  onSelect(loc.id);
                  setIsOpen(false);
                  setSearchFilter('');
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${
                  selectedLocationId === loc.id ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-900 dark:text-gray-100'
                }`}
              >
                {loc.name} {loc.zone ? <span className="text-xs text-gray-500 dark:text-gray-400">(Zone: {loc.zone})</span> : ''}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
});

// Isolated print modal — manages all print state internally so parent doesn't re-render
const PrintModal = memo(({ show, items, vendorId, apiUrl, onClose }: {
  show: boolean;
  items: PrintItem[];
  vendorId: string;
  apiUrl: string;
  onClose: () => void;
}) => {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [layouts, setLayouts] = useState<PrintLayout[]>([]);
  const [selectedPrinter, setSelectedPrinter] = useState<string>(() => localStorage.getItem('selectedPrinter') || '');
  const [selectedLayout, setSelectedLayout] = useState<string>(() => localStorage.getItem('selectedLayout') || '');
  const [localItems, setLocalItems] = useState<PrintItem[]>([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [loadingLayouts, setLoadingLayouts] = useState(false);
  const [showMissingSKUs, setShowMissingSKUs] = useState(false);
  const [missingSKUs, setMissingSKUs] = useState<string[]>([]);
  const [isCheckingSKUs, setIsCheckingSKUs] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Sync local items when parent passes new items
  useEffect(() => {
    if (show && items.length > 0) {
      setLocalItems(items);
      setShowMissingSKUs(false);
      setMissingSKUs([]);
      // Fetch printers and layouts
      fetchPrinters();
      fetchLayouts();
    }
  }, [show, items]);

  // Persist selections
  useEffect(() => { if (selectedPrinter) localStorage.setItem('selectedPrinter', selectedPrinter); }, [selectedPrinter]);
  useEffect(() => { if (selectedLayout) localStorage.setItem('selectedLayout', selectedLayout); }, [selectedLayout]);

  const fetchPrinters = async () => {
    setLoadingPrinters(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${apiUrl}/simplerfid/printers/${vendorId}`, { headers: { Authorization: `Bearer ${token}` } });
      const raw = res.data.printers || [];
      setPrinters(raw.map((p: any) => ({
        id: p.serialNumber || p.id || '',
        name: p.displayName || p.name || 'Unknown Printer',
        location: p.location || '',
        type: 'Print Station',
        status: p.status || 'Unknown',
        serialNumber: p.serialNumber,
        clientId: p.clientId,
      })));
    } catch { toast.error('Failed to load printers'); }
    finally { setLoadingPrinters(false); }
  };

  const fetchLayouts = async () => {
    setLoadingLayouts(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${apiUrl}/simplerfid/layouts/${vendorId}`, { headers: { Authorization: `Bearer ${token}` } });
      const raw = res.data.layouts || [];
      setLayouts(raw.map((l: any) => ({
        id: String(l.id || ''),
        name: l.name || 'Unnamed Layout',
        width: String(l.width || ''),
        height: String(l.height || ''),
        dpi: String(l.dpi || ''),
        format: l.formatType || l.format || '',
      })));
    } catch { toast.error('Failed to load layouts'); }
    finally { setLoadingLayouts(false); }
  };

  const updateQty = (sku: string, qty: number) => {
    setLocalItems(prev => prev.map(item => item.sku === sku ? { ...item, qty: Math.max(1, qty) } : item));
  };

  const executePrintJob = async () => {
    setIsPrinting(true);
    try {
      const token = localStorage.getItem('token');
      const printer = printers.find(p => p.id === selectedPrinter);
      const res = await axios.post(`${apiUrl}/simplerfid/print`, {
        vendor_id: vendorId,
        printer_id: selectedPrinter,
        client_id: printer?.clientId || '',
        layout_id: selectedLayout,
        items: localItems.map(item => ({
          sku: item.sku, style_number: item.sku, description: item.name,
          color: item.color, size: item.size, quantity: item.qty,
        })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) {
        const totalLabels = localItems.reduce((sum, i) => sum + i.qty, 0);
        toast.success(`Print job sent! ${totalLabels} label${totalLabels !== 1 ? 's' : ''} queued.`);
        onClose();
      } else {
        toast.error(res.data.error || 'Print job failed');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to send print job');
    } finally { setIsPrinting(false); }
  };

  const handleSend = async () => {
    if (!selectedPrinter) { toast.error('Please select a printer'); return; }
    if (!selectedLayout) { toast.error('Please select a layout'); return; }
    // Check SKUs
    setIsCheckingSKUs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${apiUrl}/simplerfid/check-skus`, {
        vendor_id: vendorId, skus: localItems.map(i => i.sku),
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.all_exist) {
        setIsCheckingSKUs(false);
        await executePrintJob();
      } else {
        setMissingSKUs(res.data.missing || []);
        setShowMissingSKUs(true);
        setIsCheckingSKUs(false);
      }
    } catch {
      toast.error('Failed to verify SKUs in SimpleRFID');
      setIsCheckingSKUs(false);
    }
  };

  const handleImportAndPrint = async () => {
    setIsImporting(true);
    try {
      const token = localStorage.getItem('token');
      for (const sku of missingSKUs) {
        const item = localItems.find(i => i.sku === sku);
        await axios.post(`${apiUrl}/products/import-single`, {
          vendor_id: vendorId, sku, name: item?.name || sku, color: item?.color || '', size: item?.size || '',
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success(`Imported ${missingSKUs.length} product${missingSKUs.length !== 1 ? 's' : ''}`);
      setShowMissingSKUs(false);
      setMissingSKUs([]);
      await executePrintJob();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to import missing products');
    } finally { setIsImporting(false); }
  };

  if (!show) return null;

  const totalLabels = localItems.reduce((sum, i) => sum + i.qty, 0);
  const busy = isPrinting || isCheckingSKUs;

  return (
    <>
      {/* Print Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800">
            <div className="flex items-center gap-3">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Print Labels</h3>
              <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium px-2.5 py-0.5 rounded-full">
                {localItems.length} item{localItems.length !== 1 ? 's' : ''}
              </span>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Print Settings */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Printer</label>
                {loadingPrinters ? (
                  <div className="h-10 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
                ) : (
                  <select value={selectedPrinter} onChange={(e) => setSelectedPrinter(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="">Select printer...</option>
                    {printers.map(p => (
                      <option key={p.id} value={p.id}>{p.name} {p.status === 'Online' ? '🟢' : '🔴'}</option>
                    ))}
                  </select>
                )}
                {selectedPrinter && (() => {
                  const p = printers.find(pr => pr.id === selectedPrinter);
                  return p ? (
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {p.status === 'Online' ? <span className="text-green-600 dark:text-green-400">● Online</span> : <span className="text-red-500">● {p.status}</span>}
                      {p.serialNumber && <span className="ml-2">SN: {p.serialNumber}</span>}
                    </div>
                  ) : null;
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Layout</label>
                {loadingLayouts ? (
                  <div className="h-10 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
                ) : (
                  <select value={selectedLayout} onChange={(e) => setSelectedLayout(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="">Select layout...</option>
                    {layouts.map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                  </select>
                )}
                {selectedLayout && (() => {
                  const l = layouts.find(ly => ly.id === selectedLayout);
                  return l ? (
                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>{l.width}" × {l.height}"</span><span>•</span><span>{l.dpi} DPI</span>
                      {l.format && <><span>•</span><span className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase">{l.format}</span></>}
                    </div>
                  ) : null;
                })()}
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="flex-1 overflow-y-auto px-6 py-3">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 pr-3">SKU</th>
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Color</th>
                  <th className="py-2 pr-3">Size</th>
                  <th className="py-2 pr-3 text-center w-24">Labels</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {localItems.map(item => (
                  <tr key={item.sku} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="py-2.5 pr-3 font-mono text-xs font-medium text-gray-900 dark:text-gray-100">{item.sku}</td>
                    <td className="py-2.5 pr-3 text-gray-700 dark:text-gray-300 max-w-[200px] truncate">{item.name || '—'}</td>
                    <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-400">{item.color || '—'}</td>
                    <td className="py-2.5 pr-3 text-gray-600 dark:text-gray-400">{item.size || '—'}</td>
                    <td className="py-2.5 pr-3 text-center">
                      <input type="number" min={1} value={item.qty}
                        onChange={(e) => updateQty(item.sku, parseInt(e.target.value) || 1)}
                        className="w-16 px-2 py-1 text-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total labels: <span className="font-semibold text-gray-900 dark:text-gray-100">{totalLabels}</span>
            </div>
            <div className="flex gap-3">
              <button onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium text-sm">
                Cancel
              </button>
              <button onClick={handleSend} disabled={!selectedPrinter || !selectedLayout || busy}
                className={`px-5 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors ${
                  !selectedPrinter || !selectedLayout || busy
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}>
                {isPrinting ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Sending...</>
                ) : isCheckingSKUs ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Checking SKUs...</>
                ) : (
                  <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>Send Print Job</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Missing SKUs Sub-Modal */}
      {showMissingSKUs && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-lg w-full">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-amber-900/20">
              <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Missing Products</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{missingSKUs.length} SKU{missingSKUs.length !== 1 ? 's' : ''} not found in SimpleRFID</p>
              </div>
            </div>
            <div className="px-6 py-4 max-h-60 overflow-y-auto">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">The following products need to be imported to SimpleRFID before printing:</p>
              <div className="space-y-2">
                {missingSKUs.map(sku => {
                  const item = localItems.find(i => i.sku === sku);
                  return (
                    <div key={sku} className="flex items-center gap-3 p-2.5 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">{sku}</div>
                        {item?.name && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.name}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => { setShowMissingSKUs(false); setMissingSKUs([]); }} disabled={isImporting}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium text-sm">
                Cancel
              </button>
              <button onClick={handleImportAndPrint} disabled={isImporting}
                className={`px-5 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors ${
                  isImporting ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600 text-white'
                }`}>
                {isImporting ? (
                  <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Importing...</>
                ) : (
                  <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>Import & Print</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

function CompareSync() {
  const { user } = useAuth();
  const { selectedVendorId: selectedVendor } = useVendor();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'unmatched' | 'matched' | 'not_imported' | 'history'>('unmatched');
  const [loading, setLoading] = useState(false);
  const [syncHistory, setSyncHistory] = useState<any[]>([]);
  const [selectedHistoryItems, setSelectedHistoryItems] = useState<Set<string>>(new Set());
  const [expandedHistoryEntry, setExpandedHistoryEntry] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationType, setSelectedLocationType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [cachedLocationName, setCachedLocationName] = useState<string | null>(() => {
    // Initialize from localStorage to prevent flash
    const userId = localStorage.getItem('token') ? 'temp' : null;
    return userId ? localStorage.getItem(`selectedLocationName_${userId}`) : null;
  });
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>('location');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isLocationSelectorCollapsed, setIsLocationSelectorCollapsed] = useState(false);
  const [locationBadgeFlashing, setLocationBadgeFlashing] = useState(false);
  const [locationContainerGlowing, setLocationContainerGlowing] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [differenceFilter, setDifferenceFilter] = useState<'all' | 'inflated' | 'deflated'>('all');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const progressRef = useRef(0);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [customItemsInput, setCustomItemsInput] = useState('10');
  const [customPageInput, setCustomPageInput] = useState('1');
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [stickyBarExpanded, setStickyBarExpanded] = useState(false);
  const [historyStickyBarExpanded, setHistoryStickyBarExpanded] = useState(false);
  const actionButtonsRef = useRef<HTMLDivElement>(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [itemsNeedingLocation, setItemsNeedingLocation] = useState<any[]>([]);
  const [originalSyncItems, setOriginalSyncItems] = useState<any[]>([]); // Store original items for sync
  const [availableLocations, setAvailableLocations] = useState<any[]>([]);
  const [showClearHistoryModal, setShowClearHistoryModal] = useState(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showHistoryMenu, setShowHistoryMenu] = useState(false);
  const [showSyncDropdown, setShowSyncDropdown] = useState(false);
  const [locationSelections, setLocationSelections] = useState<Map<string, string>>(new Map());
  const [showSyncProgress, setShowSyncProgress] = useState(false);
  const [syncProgressItems, setSyncProgressItems] = useState<any[]>([]);
  const progressPollRef = useRef<NodeJS.Timeout | null>(null);
  const [isComparing, setIsComparing] = useState(false);
  const sessionExpiredShownRef = useRef(false);
  const syncDropdownRef = useRef<HTMLDivElement>(null);
  
  // Column visibility state
  const [tableColumns, setTableColumns] = useState<Column[]>([
    { id: 'image', label: 'Image', visible: true },
    { id: 'sku', label: 'SKU', visible: true, required: true },
    { id: 'name', label: 'Name', visible: true },
    { id: 'color', label: 'Color', visible: true },
    { id: 'size', label: 'Size', visible: true },
    { id: 'location', label: 'Location', visible: true },
    { id: 'simplerfid', label: 'SimpleRFID', visible: true },
    { id: 'integration', label: 'Integration', visible: true },
    { id: 'difference', label: 'Difference', visible: true },
  ]);
  
  
  // ShipHero Inventory Snapshot option - uses less API credits but takes longer
  const [useSnapshot, setUseSnapshot] = useState(true);

  // Print modal state — all internal state lives in the memoized PrintModal component
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [itemsToPrint, setItemsToPrint] = useState<PrintItem[]>([]);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api';
  const BASE_URL = API_URL.replace('/api', ''); // Backend base URL without /api

  // Hard-coded ShipHero integration helpers
  const getIntegrationIcon = () => '/shiphero_icon.svg';
  const getIntegrationName = () => 'ShipHero';

  // Handle authentication errors with a single toast message
  const handleAuthError = () => {
    if (!sessionExpiredShownRef.current) {
      sessionExpiredShownRef.current = true;
      toast.error('Session expired. Please log back in.');
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  // Animate progress smoothly
  const animateProgress = (targetProgress: number) => {
    // Clear any existing timer
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
    }

    const currentProgress = progressRef.current;
    const increment = targetProgress > currentProgress ? 1 : -1;
    const steps = Math.abs(targetProgress - currentProgress);
    const stepDuration = steps > 0 ? 400 / steps : 0; // Spread animation over 400ms

    if (steps === 0) {
      setLoadingProgress(targetProgress);
      progressRef.current = targetProgress;
      return;
    }

    let step = 0;
    progressTimerRef.current = setInterval(() => {
      step++;
      const newProgress = currentProgress + (increment * step);
      progressRef.current = newProgress;
      setLoadingProgress(newProgress);
      
      if (step >= steps) {
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
        }
        progressRef.current = targetProgress;
        setLoadingProgress(targetProgress);
      }
    }, stepDuration);
  };

  // Sort handler (memoized)
  const handleSort = useCallback((column: string) => {
    setSortColumn(prev => {
      if (prev === column) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return prev;
      }
      setSortDirection('asc');
      return column;
    });
  }, []);

  // Get current items based on active tab (memoized)
  const currentItems = useMemo(() => {
    if (!comparisonData) return [];
    return activeTab === 'unmatched' 
      ? comparisonData.unmatched_items 
      : activeTab === 'matched' 
      ? comparisonData.matched_items 
      : comparisonData.not_imported_items || [];
  }, [comparisonData, activeTab]);

  // Get inflated count (positive differences) — memoized
  const inflatedCount = useMemo(() => {
    if (!comparisonData?.unmatched_items) return 0;
    return comparisonData.unmatched_items.filter((item: any) => {
      const diff = item.difference ?? item.Difference ?? 0;
      return diff > 0;
    }).length;
  }, [comparisonData]);

  // Get deflated count (negative differences) — memoized
  const deflatedCount = useMemo(() => {
    if (!comparisonData?.unmatched_items) return 0;
    return comparisonData.unmatched_items.filter((item: any) => {
      const diff = item.difference ?? item.Difference ?? 0;
      return diff < 0;
    }).length;
  }, [comparisonData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, differenceFilter, activeTab]);

  // Close history menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showHistoryMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.history-menu-container')) {
          setShowHistoryMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHistoryMenu]);


  // Sync custom page input with current page
  useEffect(() => {
    setCustomPageInput(currentPage.toString());
  }, [currentPage]);

  // Reset any stale comparison state on mount
  // This prevents showing old comparison progress when refreshing the page
  useEffect(() => {
    const resetStaleProgress = async () => {
      try {
        const token = localStorage.getItem('token');
        // Reset progress state on page load to clear any stale data
        await axios.post(`${API_URL}/compare/cancel`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        // Ignore errors - just ensuring clean state
      }
    };
    
    resetStaleProgress();
  }, []);

  // Start polling for progress updates
  const startProgressPolling = () => {
    if (progressPollRef.current) {
      clearInterval(progressPollRef.current);
    }
    
    progressPollRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('token');
        const progressResp = await axios.get(`${API_URL}/compare/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const progress = progressResp.data;
        
        if (progress.is_cancelled) {
          // Operation was cancelled
          if (progressPollRef.current) {
            clearInterval(progressPollRef.current);
            progressPollRef.current = null;
          }
          setLoading(false);
          setIsComparing(false);
          toast.error('Comparison cancelled');
          return;
        }
        
        if (progress.locations_loaded > 0 || progress.products_loaded > 0) {
          const statusMsg = progress.current_status || 
            `Loading: ${progress.locations_loaded} locations, ${progress.products_loaded} products (${progress.elapsed_seconds?.toFixed(1)}s)`;
          setLoadingStatus(statusMsg);
          
          const locProgress = Math.min(progress.locations_loaded / 5000, 0.25) * 100;
          const prodProgress = Math.min(progress.products_loaded / 10000, 0.25) * 100;
          const totalProgress = Math.min(30 + locProgress + prodProgress, 85);
          animateProgress(totalProgress);
        }
        
        if (progress.is_complete) {
          if (progressPollRef.current) {
            clearInterval(progressPollRef.current);
            progressPollRef.current = null;
          }
          // Comparison completed - but we might not have the data yet
          // Keep loading state until we get the results
        }
      } catch (err) {
        // Ignore progress errors
      }
    }, 1000);
  };

  // Cancel comparison handler
  const handleCancelCompare = async () => {
    console.log('Cancel button clicked');
    
    // Always clean up frontend state first
    if (progressPollRef.current) {
      clearInterval(progressPollRef.current);
      progressPollRef.current = null;
    }
    
    setLoading(false);
    setIsComparing(false);
    
    // If no comparison data exists, re-expand the location selector so user can try again
    if (!comparisonData) {
      setIsLocationSelectorCollapsed(false);
    }
    
    // Show toast immediately
    toast('Comparison cancelled', { icon: 'ℹ️' });
    
    // Try to notify backend, but don't fail if it errors (cancellation already happened on frontend)
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/compare/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Backend cancel notification sent successfully');
    } catch (error: any) {
      // Log but don't show error - cancellation already worked on frontend
      console.log('Backend cancel notification failed (cancellation still successful):', error.message);
    }
  };

  // Filter and sort items (memoized — biggest perf win, was called 4+ times per render)
  const filteredAndSortedItems = useMemo(() => {
    let items = [...currentItems];
    
    // Apply search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      items = items.filter((item: any) => {
        const sku = String(item.sku || item.SKU || '').toLowerCase();
        const name = String(item.name || item.Name || '').toLowerCase();
        return sku.includes(search) || name.includes(search);
      });
    }
    
    // Apply difference filter
    if (differenceFilter !== 'all') {
      items = items.filter((item: any) => {
        const diff = item.difference ?? item.Difference ?? 0;
        if (differenceFilter === 'inflated') return diff > 0;
        if (differenceFilter === 'deflated') return diff < 0;
        return true;
      });
    }
    
    // Apply sorting
    if (sortColumn) {
      items.sort((a: any, b: any) => {
        let aVal, bVal;
        let isLocationSort = false;
        
        switch (sortColumn) {
          case 'sku':
            aVal = String(a.sku || a.SKU || '');
            bVal = String(b.sku || b.SKU || '');
            break;
          case 'name':
            aVal = String(a.name || a.Name || '');
            bVal = String(b.name || b.Name || '');
            break;
          case 'location':
            aVal = String(a.location_name || a.LocationName || a.location_id || a.LocationID || '');
            bVal = String(b.location_name || b.LocationName || b.location_id || b.LocationID || '');
            isLocationSort = true;
            break;
          case 'color':
            aVal = String(a.color || a.Color || '');
            bVal = String(b.color || b.Color || '');
            break;
          case 'size':
            aVal = String(a.size || a.Size || '');
            bVal = String(b.size || b.Size || '');
            break;
          case 'simplerfid':
            aVal = a.simplerfid_qty ?? a.SimpleRFIDQty ?? 0;
            bVal = b.simplerfid_qty ?? b.SimpleRFIDQty ?? 0;
            break;
          case 'shiphero':
            aVal = a.shiphero_qty ?? a.ShipHeroQty ?? 0;
            bVal = b.shiphero_qty ?? b.ShipHeroQty ?? 0;
            break;
          case 'difference':
            aVal = a.difference ?? a.Difference ?? 0;
            bVal = b.difference ?? b.Difference ?? 0;
            break;
          default:
            return 0;
        }
        
        if (typeof aVal === 'string') {
          const aEmpty = !aVal || aVal === '';
          const bEmpty = !bVal || bVal === '';
          if (aEmpty && !bEmpty) return 1;
          if (!aEmpty && bEmpty) return -1;
          if (aEmpty && bEmpty) return 0;
          if (isLocationSort) {
            return sortDirection === 'asc' 
              ? aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' }) 
              : bVal.localeCompare(aVal, undefined, { numeric: true, sensitivity: 'base' });
          }
          return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        } else {
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }
      });
    }
    
    return items;
  }, [currentItems, searchTerm, differenceFilter, sortColumn, sortDirection]);

  // Paginated items (memoized)
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedItems, currentPage, itemsPerPage]);

  // Select-all checkbox state (memoized)
  const allPageSelected = useMemo(() => {
    return paginatedItems.length > 0 && paginatedItems.every((item: any) => 
      selectedItems.has(String(item.sku || item.SKU))
    );
  }, [paginatedItems, selectedItems]);

  // Total pages (memoized)
  const totalItems = filteredAndSortedItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Handle items per page change
  const handleItemsPerPageChange = (value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num > 0) {
      setItemsPerPage(num);
      setCustomItemsInput(value);
      setCurrentPage(1);
    }
  };

  const handlePageChange = (value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num > 0 && num <= totalPages) {
      setCurrentPage(num);
      setCustomPageInput(value);
    }
  };

  // Selection handlers (memoized)
  const toggleSelectItem = useCallback((sku: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(sku)) {
        next.delete(sku);
      } else {
        next.add(sku);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const currentSKUs = paginatedItems.map((item: any) => String(item.sku || item.SKU));
    
    setSelectedItems(prev => {
      const allSelected = currentSKUs.every((sku: string) => prev.has(sku));
      const next = new Set(prev);
      if (allSelected) {
        currentSKUs.forEach((sku: string) => next.delete(sku));
      } else {
        currentSKUs.forEach((sku: string) => next.add(sku));
      }
      return next;
    });
  }, [paginatedItems]);

  const clearAllSelections = () => {
    setSelectedItems(new Set());
  };

  // Get selected items data
  const getSelectedItemsData = () => {
    return currentItems.filter((item: any) => selectedItems.has(String(item.sku || item.SKU)));
  };

  // Check if items need location assignments before syncing
  const checkItemsNeedingLocations = async (items: any[]) => {
    if (!selectedWarehouse) return [];

    const token = localStorage.getItem('token');
    const itemsNeedingLoc: any[] = [];

    for (const item of items) {
      const shipheroQty = item.shiphero_qty ?? item.ShipHeroQty ?? 0;
      const itemLocationId = item.location_id || item.LocationID;

      // If item has ShipHero qty = 0 and no location, it needs one
      if (shipheroQty === 0 && (!itemLocationId || itemLocationId.trim() === '')) {
        // Check if we have a saved location preference
        try {
          const response = await axios.get(
            `${API_URL}/locations/preference?sku=${item.sku || item.SKU}&warehouse_id=${selectedWarehouse.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (!response.data.location_id) {
            // No saved preference, needs location selection
            itemsNeedingLoc.push(item);
          }
        } catch (error) {
          // Error checking preference, assume needs location
          itemsNeedingLoc.push(item);
        }
      }
    }

    return itemsNeedingLoc;
  };

  // Sync handler - accepts optional items array, otherwise uses selected items
  const handleSync = async (itemsToSync?: any[]) => {
    const items = itemsToSync || getSelectedItemsData();
    if (items.length === 0) {
      toast.error('Please select items to sync');
      return;
    }

    try {
      // Check if warehouse/location is selected
      if (!selectedWarehouse) {
        toast.error('Please select a ShipHero warehouse before syncing');
        return;
      }

      // Pre-validate: Check if any items need location assignments
      setLoading(true);
      setLoadingProgress(0);
      setLoadingStatus('Validating items...');
      
      const needingLocations = await checkItemsNeedingLocations(items);
      
      if (needingLocations.length > 0) {
        // Items need locations - show location selector immediately
        console.log(`${needingLocations.length} items need location assignments out of ${items.length} total items`);
        setItemsNeedingLocation(needingLocations);
        setOriginalSyncItems(items); // Store ALL selected items for sync after location assignment
        
        // Fetch available locations
        const token = localStorage.getItem('token');
        const locationsResponse = await axios.get(
          `${API_URL}/locations?warehouse_id=${selectedWarehouse.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const allLocations = locationsResponse.data.locations || [];
        setAvailableLocations(allLocations);
        
        // Auto-assign scan-4-me location for items that need a location
        const scan4meLocation = allLocations.find((loc: any) => 
          (loc.name || '').toLowerCase() === 'scan-4-me'
        );
        
        if (scan4meLocation) {
          // Auto-assign scan-4-me to all items needing locations
          const autoSelections = new Map<string, string>();
          for (const item of needingLocations) {
            const sku = item.sku || item.SKU;
            autoSelections.set(sku, scan4meLocation.id);
          }
          setLocationSelections(autoSelections);
          
          // Proceed directly with sync using scan-4-me
          toast.success(`Auto-assigned ${needingLocations.length} item${needingLocations.length !== 1 ? 's' : ''} to scan-4-me location`);
          
          // Save location preferences and sync
          setShowLocationModal(false);
          setLoading(false);
          setLoadingProgress(0);
          setLoadingStatus('');
          
          // Trigger the save + sync flow with pre-filled selections
          setTimeout(() => handleSaveLocationsAndSyncWithSelections(items, needingLocations, autoSelections), 100);
          return;
        }
        
        // No scan-4-me found - fall back to manual location selection
        setShowLocationModal(true);
        setLoading(false);
        setLoadingProgress(0);
        setLoadingStatus('');
        return; // Exit sync, wait for user to assign locations
      }

      setLoadingProgress(20);
      setLoadingStatus(`Syncing ${items.length} item${items.length !== 1 ? 's' : ''} to ${getIntegrationName()}...`);
      
      // Format items for sync - each item uses its current location in ShipHero
      const syncItems = items.map((item: any) => ({
        sku: String(item.sku || item.SKU),
        name: item.name || item.Name || '',
        warehouse_id: selectedWarehouse.id,
        location_id: item.location_id || item.LocationID, // Use item's current location
        quantity: item.simplerfid_qty ?? item.SimpleRFIDQty ?? 0,
        previous_qty: item.shiphero_qty ?? item.ShipHeroQty ?? 0
      }));

      const payload = { items: syncItems };
      console.log('Sending sync request:', JSON.stringify(payload, null, 2));
      console.log('Number of items:', syncItems.length);
      console.log('First item example:', syncItems[0]);

      const token = localStorage.getItem('token');
      setLoadingProgress(40);
      setLoadingStatus(`Sending sync data to ${getIntegrationName()} servers...`);
      
      const response = await axios.post(`${API_URL}/sync`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLoadingProgress(80);
      setLoadingStatus('Processing sync results...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      setLoadingProgress(100);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStatus('');
      
      // Show results
      const successCount = response.data.success_count || 0;
      const failureCount = response.data.failure_count || 0;
      
      // Check if any items failed due to missing location
      const failedItems = response.data.results?.filter((r: any) => 
        r.status === 'error' && r.message?.includes('No location found')
      ) || [];
      
      if (failedItems.length > 0) {
        // Show location selection modal
        console.log('Items need location selection:', failedItems);
        console.log('Selected warehouse:', selectedWarehouse);
        setItemsNeedingLocation(failedItems);
        
        // Fetch available locations for the warehouse
        console.log(`Fetching locations for warehouse: ${selectedWarehouse.id}`);
        const locationsResponse = await axios.get(`${API_URL}/locations?warehouse_id=${selectedWarehouse.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('Locations response:', locationsResponse.data);
        setAvailableLocations(locationsResponse.data.locations || []);
        setShowLocationModal(true);
        
        if (successCount > 0) {
          toast.success(`${successCount} items synced successfully. ${failedItems.length} items need location selection.`);
        } else {
          toast.error(`${failedItems.length} items need location selection before syncing.`);
        }
      } else if (successCount > 0) {
        toast.success(`Successfully synced ${successCount} item${successCount !== 1 ? 's' : ''} to ShipHero!${failureCount > 0 ? ` (${failureCount} failed)` : ''}`);
        // Clear selections after successful sync
        clearAllSelections();
      } else {
        toast.error(`Sync failed for all ${failureCount} items. Check console for details.`);
      }
      
      console.log('Sync results:', response.data);
      
      // Update comparison data dynamically without re-fetching
      if (successCount > 0 && response.data.results) {
        console.log('Updating comparison data dynamically after sync...');
        updateComparisonDataAfterSync(response.data.results);
      }
    } catch (error: any) {
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStatus('');
      console.error('Sync error:', error);
      toast.error(error.response?.data?.error || 'Failed to sync inventory');
      clearAllSelections();
    }
  };

  // Sync selected items (items with checked checkboxes)
  const handleSyncSelected = async () => {
    setShowSyncDropdown(false);
    const items = getSelectedItemsData();
    if (items.length === 0) {
      toast.error('Please select items to sync');
      return;
    }
    await handleSync(items);
  };

  // Sync all filtered items (all items in current filtered view, across all pages)
  const handleSyncFiltered = async () => {
    setShowSyncDropdown(false);
    const items = filteredAndSortedItems;
    if (items.length === 0) {
      toast.error('No items to sync in current filtered view');
      return;
    }
    await handleSync(items);
  };

  // Sync everything (all items in current tab, ignoring all filters)
  const handleSyncEverything = async () => {
    setShowSyncDropdown(false);
    const items = currentItems;
    if (items.length === 0) {
      toast.error('No items to sync in current tab');
      return;
    }
    await handleSync(items);
  };

  // Print handler — opens isolated PrintModal component
  const handlePrint = () => {
    const items = getSelectedItemsData();
    if (items.length === 0) {
      toast.error('Please select items to print');
      return;
    }
    if (!selectedVendor) {
      toast.error('Please select a SimpleRFID vendor first');
      return;
    }
    setItemsToPrint(items.map((item: any) => ({
      sku: item.sku || item.SKU || '',
      name: item.name || item.Name || item.title || '',
      color: item.color || item.Color || '',
      size: item.size || item.Size || '',
      qty: Math.max(1, item.simplerfid_qty || item.SimpleRFIDQty || 1),
    })));
    setShowPrintModal(true);
  };

  // ==================== IMPORT HANDLERS ====================
  
  // Get selected import items data
  // ==================== END IMPORT HANDLERS ====================

  // Helper function to update comparison data after sync/revert
  const updateComparisonDataAfterSync = (syncedItems: any[]) => {
    if (!comparisonData) return;

    console.log('=== Starting updateComparisonDataAfterSync ===');
    console.log('Total synced items received:', syncedItems.length);
    console.log('Synced items:', syncedItems);

    const updatedData = { ...comparisonData };
    const successfulItems = syncedItems.filter(item => item.status === 'success');
    console.log('Successful syncs:', successfulItems.length);
    
    const successfulSKUs = new Set(successfulItems.map(item => item.sku));
    console.log('Successful SKUs:', Array.from(successfulSKUs));

    // Move successfully synced items from unmatched to matched
    const remainingUnmatched: any[] = [];
    const newlyMatched: any[] = [];

    updatedData.unmatched_items?.forEach((item: any) => {
      if (successfulSKUs.has(item.sku || item.SKU)) {
        // Update the item to reflect matched quantities
        const syncedItem = syncedItems.find(si => si.sku === (item.sku || item.SKU));
        if (syncedItem) {
          // Preserve the original SimpleRFID quantity
          const originalSimpleRFIDQty = item.simplerfid_qty ?? item.SimpleRFIDQty ?? 0;
          
          console.log(`Processing synced item ${item.sku}:`, {
            originalSimpleRFIDQty,
            quantity_after: syncedItem.quantity_after,
            originalItem: item
          });
          
          newlyMatched.push({
            ...item,
            shiphero_qty: syncedItem.quantity_after,
            ShipHeroQty: syncedItem.quantity_after,
            simplerfid_qty: originalSimpleRFIDQty,
            SimpleRFIDQty: originalSimpleRFIDQty,
            difference: 0
          });
        }
      } else {
        remainingUnmatched.push(item);
      }
    });

    const allMatchedItems = [...(updatedData.matched_items || []), ...newlyMatched];
    
    // Create a completely new object to ensure React detects the change
    const newComparisonData = {
      ...updatedData,
      unmatched_items: remainingUnmatched,
      matched_items: allMatchedItems,
      unmatched_count: remainingUnmatched.length,
      matched_count: allMatchedItems.length
    };
    
    console.log(`Moved ${newlyMatched.length} items from unmatched to matched`);
    console.log('Final matched items count:', allMatchedItems.length);
    console.log('Final unmatched items count:', remainingUnmatched.length);
    console.log('Newly matched items:', newlyMatched);
    console.log('=== Finished updateComparisonDataAfterSync ===');
    
    setComparisonData(newComparisonData);
  };

  // Helper function to update comparison data after revert
  const updateComparisonDataAfterRevert = (revertedItems: any[]) => {
    if (!comparisonData) return;

    console.log('=== Starting updateComparisonDataAfterRevert ===');
    console.log('Total reverted items received:', revertedItems.length);
    console.log('Reverted items:', revertedItems);
    console.log('First reverted item structure:', JSON.stringify(revertedItems[0], null, 2));

    const updatedData = { ...comparisonData };
    const successfulReverts = revertedItems.filter(item => item.status === 'success');
    console.log('Successful reverts:', successfulReverts.length);
    
    const revertedMap = new Map(
      successfulReverts.map(item => [item.sku, item])
    );
    console.log('Reverted map keys:', Array.from(revertedMap.keys()));

    const newMatched: any[] = [];
    const newUnmatched: any[] = [];
    let movedToMatched = 0;
    let movedToUnmatched = 0;
    let stayedInMatched = 0;
    let stayedInUnmatched = 0;

    // Process matched items
    updatedData.matched_items?.forEach((item: any) => {
      const sku = item.sku || item.SKU;
      const revertedItem = revertedMap.get(sku);
      
      if (revertedItem) {
        // Item was reverted - recalculate difference
        const simpleRFIDQty = item.simplerfid_qty ?? item.SimpleRFIDQty ?? 0;
        
        // Extract quantity_after from revert response (backend now provides this)
        const revertedShipHeroQty = revertedItem.quantity_after ?? 0;
        const newDifference = revertedShipHeroQty - simpleRFIDQty;  // ShipHero - SimpleRFID
        
        console.log(`Reverting matched item ${sku}:`, {
          simpleRFIDQty,
          revertedShipHeroQty,
          newDifference,
          revertedItem
        });
        
        const updatedItem = {
          ...item,
          shiphero_qty: revertedShipHeroQty,
          ShipHeroQty: revertedShipHeroQty,
          difference: newDifference,
          // Ensure simplerfid quantities are preserved with both naming conventions
          simplerfid_qty: simpleRFIDQty,
          SimpleRFIDQty: simpleRFIDQty
        };

        if (newDifference === 0) {
          // Still matched after revert
          newMatched.push(updatedItem);
          stayedInMatched++;
        } else {
          // Now unmatched after revert
          newUnmatched.push(updatedItem);
          movedToUnmatched++;
        }
      } else {
        // Item wasn't reverted, keep in matched
        newMatched.push(item);
      }
    });

    // Process unmatched items
    updatedData.unmatched_items?.forEach((item: any) => {
      const sku = item.sku || item.SKU;
      const revertedItem = revertedMap.get(sku);
      
      if (revertedItem) {
        // Item was reverted - recalculate difference
        const simpleRFIDQty = item.simplerfid_qty ?? item.SimpleRFIDQty ?? 0;
        
        // Extract quantity_after from revert response (backend now provides this)
        const revertedShipHeroQty = revertedItem.quantity_after ?? 0;
        const newDifference = revertedShipHeroQty - simpleRFIDQty;  // ShipHero - SimpleRFID
        
        console.log(`Reverting unmatched item ${sku}:`, {
          simpleRFIDQty,
          revertedShipHeroQty,
          newDifference,
          revertedItem
        });
        
        const updatedItem = {
          ...item,
          shiphero_qty: revertedShipHeroQty,
          ShipHeroQty: revertedShipHeroQty,
          difference: newDifference,
          // Ensure simplerfid quantities are preserved with both naming conventions
          simplerfid_qty: simpleRFIDQty,
          SimpleRFIDQty: simpleRFIDQty
        };

        if (newDifference === 0) {
          // Now matched after revert
          newMatched.push(updatedItem);
          movedToMatched++;
        } else {
          // Still unmatched after revert
          newUnmatched.push(updatedItem);
          stayedInUnmatched++;
        }
      } else {
        // Item wasn't reverted, keep in unmatched
        newUnmatched.push(item);
      }
    });

    // Create a completely new object to ensure React detects the change
    const newComparisonData = {
      ...updatedData,
      matched_items: newMatched,
      unmatched_items: newUnmatched,
      matched_count: newMatched.length,
      unmatched_count: newUnmatched.length
    };
    
    console.log(`Revert results: ${movedToMatched} to matched, ${movedToUnmatched} to unmatched, ${stayedInMatched} stayed matched, ${stayedInUnmatched} stayed unmatched`);
    console.log('Final matched items count:', newMatched.length);
    console.log('Final unmatched items count:', newUnmatched.length);
    console.log('=== Finished updateComparisonDataAfterRevert ===');
    
    setComparisonData(newComparisonData);
  };

  // Fetch sync history
  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/sync/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyncHistory(response.data.history || []);
    } catch (error: any) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load sync history');
    }
  };

  // Revert entire sync
  const handleRevertEntireSync = async (historyId: string) => {
    if (!confirm('Are you sure you want to revert ALL items in this sync? This will restore all quantities to their previous values.')) {
      return;
    }

    try {
      setLoading(true);
      setLoadingProgress(0);
      setLoadingStatus('Preparing to revert sync...');
      
      await new Promise(resolve => setTimeout(resolve, 300));
      setLoadingProgress(20);
      setLoadingStatus(`Sending revert request to ${getIntegrationName()} servers...`);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/sync/revert/${historyId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setLoadingProgress(70);
      setLoadingStatus('Processing reverted items and updating inventory...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Update comparison data dynamically FIRST (before fetching history)
      if (comparisonData && response.data.results) {
        console.log('Updating comparison data dynamically after revert...');
        console.log('Revert response results:', response.data.results);
        updateComparisonDataAfterRevert(response.data.results);
      }
      
      setLoadingProgress(90);
      setLoadingStatus('Refreshing sync history...');
      
      // Then refresh history
      await fetchHistory();
      
      setLoadingProgress(100);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStatus('');
      
      toast.success(response.data.message || 'Sync reverted successfully!');
    } catch (error: any) {
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStatus('');
      console.error('Revert error:', error);
      toast.error(error.response?.data?.error || 'Failed to revert sync');
    }
  };

  // Show revert confirmation modal
  const handleRevertSelectedItems = () => {
    const selectedItems: string[] = Array.from(selectedHistoryItems) as string[];
    if (selectedItems.length === 0) {
      toast.error('Please select items to revert');
      return;
    }
    setShowRevertModal(true);
  };

  // Actually perform the revert after confirmation
  const confirmRevertSelected = async () => {
    const selectedItems: string[] = Array.from(selectedHistoryItems) as string[];
    setShowRevertModal(false);

    try {
      setLoading(true);
      setLoadingProgress(0);
      setLoadingStatus('Preparing to revert selected items...');
      
      await new Promise(resolve => setTimeout(resolve, 300));
      setLoadingProgress(15);
      
      // Group items by history entry
      const itemsByHistory: { [historyId: string]: string[] } = {};
      selectedItems.forEach((key) => {
        const [historyId, sku] = key.split('|||');
        if (!itemsByHistory[historyId]) {
          itemsByHistory[historyId] = [];
        }
        itemsByHistory[historyId].push(sku);
      });

      setLoadingProgress(25);
      setLoadingStatus(`Reverting ${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''}...`);

      const token = localStorage.getItem('token');
      
      // Revert each history entry's selected items and collect results
      const allResults: any[] = [];
      const historyEntries = Object.entries(itemsByHistory);
      
      for (let i = 0; i < historyEntries.length; i++) {
        const [historyId, skus] = historyEntries[i];
        const progress = 25 + Math.floor((i / historyEntries.length) * 40);
        setLoadingProgress(progress);
        setLoadingStatus(`Sending revert request ${i + 1}/${historyEntries.length} to ${getIntegrationName()}...`);
        
        const response = await axios.post(
          `${API_URL}/sync/revert/${historyId}/items`,
          { skus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.results) {
          console.log(`Revert response for history ${historyId}:`, response.data.results);
          allResults.push(...response.data.results);
        }
      }
      
      setLoadingProgress(70);
      setLoadingStatus('Processing reverted items and updating inventory...');
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Update comparison data dynamically FIRST (before fetching history)
      if (comparisonData && allResults.length > 0) {
        console.log('All revert results combined:', allResults);
        console.log('Updating comparison data dynamically after revert...');
        updateComparisonDataAfterRevert(allResults);
      }
      
      setLoadingProgress(90);
      setLoadingStatus('Refreshing sync history...');
      
      // Clear selections and refresh history
      setSelectedHistoryItems(new Set());
      await fetchHistory();
      
      setLoadingProgress(100);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStatus('');
      
      toast.success(`Successfully reverted ${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''}!`);
    } catch (error: any) {
      setLoading(false);
      setLoadingProgress(0);
      setLoadingStatus('');
      console.error('Revert error:', error);
      toast.error(error.response?.data?.error || 'Failed to revert items');
    }
  };

  // Clear all sync history
  const handleClearHistory = async () => {
    const token = localStorage.getItem('token');
    try {
      setLoading(true);
      await axios.delete(`${API_URL}/sync-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Sync history cleared successfully');
      setShowClearHistoryModal(false);
      fetchHistory(); // Refresh history
    } catch (error) {
      console.error('Failed to clear history:', error);
      toast.error('Failed to clear sync history');
    } finally {
      setLoading(false);
    }
  };

  // Get selected history items data
  const getSelectedHistoryItemsData = () => {
    const result: any[] = [];
    syncHistory.forEach((entry: any) => {
      if (entry.items) {
        entry.items.forEach((item: any) => {
          const key = `${entry.id}|||${item.sku}`;
          if (selectedHistoryItems.has(key)) {
            result.push({ ...item, historyId: entry.id, syncDate: entry.created_at });
          }
        });
      }
    });
    return result;
  };

  // Handle location selection for an item
  const handleLocationSelect = (sku: string, locationId: string) => {
    setLocationSelections(prev => {
      const newMap = new Map(prev);
      newMap.set(sku, locationId);
      return newMap;
    });
  };

  // Save a single location preference and update local state
  const handleSaveSingleLocation = async (item: any, locationId: string) => {
    if (!selectedWarehouse) return;

    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API_URL}/locations/preference`, {
        sku: item.sku || item.SKU,
        warehouse_id: selectedWarehouse.id,
        location_id: locationId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update the item in comparison data
      if (comparisonData) {
        const updatedData = { ...comparisonData };
        
        // Update in unmatched items
        if (updatedData.unmatched_items) {
          updatedData.unmatched_items = updatedData.unmatched_items.map((i: any) => 
            (i.sku || i.SKU) === (item.sku || item.SKU) 
              ? { ...i, location_id: locationId, LocationID: locationId }
              : i
          );
        }
        
        // Update in matched items
        if (updatedData.matched_items) {
          updatedData.matched_items = updatedData.matched_items.map((i: any) => 
            (i.sku || i.SKU) === (item.sku || item.SKU) 
              ? { ...i, location_id: locationId, LocationID: locationId }
              : i
          );
        }

        setComparisonData(updatedData);
        
        // Update selected item if it's still open
        if (selectedItem && (selectedItem.sku || selectedItem.SKU) === (item.sku || item.SKU)) {
          setSelectedItem({ ...selectedItem, location_id: locationId, LocationID: locationId });
        }
      }

      toast.success('Location saved successfully');
      setShowLocationModal(false);
      setLocationSelections(new Map());
    } catch (error) {
      console.error('Failed to save location:', error);
      toast.error('Failed to save location');
    }
  };

  // Save location preferences and sync with explicit parameters (used for auto-assign flow)
  const handleSaveLocationsAndSyncWithSelections = async (
    allItems: any[], 
    locItems: any[], 
    selections: Map<string, string>
  ) => {
    if (!selectedWarehouse) return;

    const token = localStorage.getItem('token');
    
    const syncItems = allItems.map((item: any) => {
      const sku = item.sku || item.SKU;
      const selectedLocationId = selections.get(sku);
      const locationId = selectedLocationId || item.location_id || item.LocationID || '';
      
      return {
        sku: String(sku),
        name: item.name || item.Name || '',
        warehouse_id: selectedWarehouse.id,
        location_id: locationId,
        quantity: item.simplerfid_qty ?? item.SimpleRFIDQty ?? 0,
        previous_qty: item.shiphero_qty ?? item.ShipHeroQty ?? 0
      };
    });

    const progressItems = syncItems.map(item => ({
      ...item,
      status: 'pending' as 'pending' | 'saving' | 'syncing' | 'success' | 'error',
      message: 'Waiting...'
    }));
    setSyncProgressItems(progressItems);
    setShowSyncProgress(true);

    let allResults: any[] = [];
    let successCount = 0;
    let failureCount = 0;

    try {
      // Save location preferences for items that needed them
      for (let i = 0; i < locItems.length; i++) {
        const item = locItems[i];
        const locationId = selections.get(item.sku || item.SKU);
        
        setSyncProgressItems(prev => prev.map((pItem, idx) => 
          idx === i ? { ...pItem, status: 'saving', message: 'Saving location preference...' } : pItem
        ));

        if (locationId) {
          try {
            await axios.post(`${API_URL}/locations/preference`, {
              sku: item.sku || item.SKU,
              warehouse_id: selectedWarehouse.id,
              location_id: locationId
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } catch (error) {
            console.error(`Failed to save location for ${item.sku || item.SKU}:`, error);
          }
        }
      }

      // Update all items to syncing status
      setSyncProgressItems(prev => prev.map(pItem => ({
        ...pItem,
        status: 'syncing',
        message: `Syncing to ${getIntegrationName()}...`
      })));

      // Sync ALL items in a single batch request
      try {
        const response = await axios.post(`${API_URL}/sync`, { 
          items: syncItems 
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        successCount = response.data.success_count || 0;
        failureCount = response.data.failure_count || 0;

        if (response.data.results && response.data.results.length > 0) {
          allResults = response.data.results;
          setSyncProgressItems(prev => prev.map(pItem => {
            const result = response.data.results.find((r: any) => r.sku === pItem.sku);
            if (result) {
              return {
                ...pItem,
                status: result.status === 'success' ? 'success' : 'error',
                message: result.status === 'success' ? 'Successfully synced!' : (result.message || 'Sync failed')
              };
            }
            return pItem;
          }));
        }
      } catch (error: any) {
        failureCount = syncItems.length;
        const errorMsg = error.response?.data?.error || error.message || 'Sync failed';
        setSyncProgressItems(prev => prev.map(pItem => ({
          ...pItem,
          status: 'error',
          message: errorMsg
        })));
      }

      if (allResults.length > 0) {
        updateComparisonDataAfterSync(allResults);
      }

      setTimeout(() => {
        setShowSyncProgress(false);
        setSyncProgressItems([]);
        setItemsNeedingLocation([]);
        setOriginalSyncItems([]);
        setLocationSelections(new Map());
        
        if (successCount > 0 && failureCount === 0) {
          toast.success(`Successfully synced ${successCount} item${successCount !== 1 ? 's' : ''}!`);
        } else if (successCount > 0 && failureCount > 0) {
          toast.success(`Synced ${successCount} item${successCount !== 1 ? 's' : ''}. ${failureCount} failed.`);
        } else {
          toast.error(`Failed to sync all ${failureCount} item${failureCount !== 1 ? 's' : ''}.`);
        }
        
        clearAllSelections();
      }, 2000);

    } catch (error: any) {
      console.error('Sync error:', error);
      setTimeout(() => {
        setShowSyncProgress(false);
        setSyncProgressItems([]);
        setItemsNeedingLocation([]);
        setOriginalSyncItems([]);
        setLocationSelections(new Map());
        toast.error('Failed to complete sync operation');
        clearAllSelections();
      }, 2000);
    }
  };

  // Save location preferences and retry sync
  const handleSaveLocationsAndSync = async () => {
    if (!selectedWarehouse) return;

    const token = localStorage.getItem('token');
    
    // Build sync items from ALL originally selected items, not just the ones that needed locations
    const itemsToSync = originalSyncItems.length > 0 ? originalSyncItems : itemsNeedingLocation;
    
    const syncItems = itemsToSync.map((item: any) => {
      const sku = item.sku || item.SKU;
      // Check if this item needed a location and has a selection
      const selectedLocationId = locationSelections.get(sku);
      // Use selected location if available, otherwise use existing location
      const locationId = selectedLocationId || item.location_id || item.LocationID || '';
      
      return {
        sku: String(sku),
        name: item.name || item.Name || '',
        warehouse_id: selectedWarehouse.id,
        location_id: locationId,
        quantity: item.simplerfid_qty ?? item.SimpleRFIDQty ?? 0,
        previous_qty: item.shiphero_qty ?? item.ShipHeroQty ?? 0
      };
    });

    // Initialize progress tracking
    const progressItems = syncItems.map(item => ({
      ...item,
      status: 'pending' as 'pending' | 'saving' | 'syncing' | 'success' | 'error',
      message: 'Waiting...'
    }));
    setSyncProgressItems(progressItems);
    setShowLocationModal(false);
    setShowSyncProgress(true);

    let allResults: any[] = [];
    let successCount = 0;
    let failureCount = 0;

    try {
      // Save each location preference with progress updates
      for (let i = 0; i < itemsNeedingLocation.length; i++) {
        const item = itemsNeedingLocation[i];
        const locationId = locationSelections.get(item.sku);
        
        // Update status to saving
        setSyncProgressItems(prev => prev.map((pItem, idx) => 
          idx === i ? { ...pItem, status: 'saving', message: 'Saving location preference...' } : pItem
        ));

        if (locationId) {
          try {
            await axios.post(`${API_URL}/locations/preference`, {
              sku: item.sku,
              warehouse_id: selectedWarehouse.id,
              location_id: locationId
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            // Update status to syncing
            setSyncProgressItems(prev => prev.map((pItem, idx) => 
              idx === i ? { ...pItem, status: 'syncing', message: `Syncing to ${getIntegrationName()}...` } : pItem
            ));
          } catch (error) {
            console.error(`Failed to save location for ${item.sku}:`, error);
          }
        }
      }

      // Update all items to syncing status
      setSyncProgressItems(prev => prev.map(pItem => ({
        ...pItem,
        status: 'syncing',
        message: `Syncing to ${getIntegrationName()}...`
      })));

      // Sync ALL items in a single batch request
      try {
        const response = await axios.post(`${API_URL}/sync`, { 
          items: syncItems 
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        successCount = response.data.success_count || 0;
        failureCount = response.data.failure_count || 0;

        // Update each item's status based on results
        if (response.data.results && response.data.results.length > 0) {
          allResults = response.data.results;
          
          // Map results back to progress items by SKU
          setSyncProgressItems(prev => prev.map(pItem => {
            const result = response.data.results.find((r: any) => r.sku === pItem.sku);
            if (result) {
              return {
                ...pItem,
                status: result.status === 'success' ? 'success' : 'error',
                message: result.status === 'success' ? 'Successfully synced!' : (result.message || 'Sync failed')
              };
            }
            return pItem;
          }));
        }
      } catch (error: any) {
        failureCount = syncItems.length;
        const errorMsg = error.response?.data?.error || error.message || 'Sync failed';
        
        // Mark all items as error
        setSyncProgressItems(prev => prev.map(pItem => ({
          ...pItem,
          status: 'error',
          message: errorMsg
        })));
      }

      // Update comparison data with all successful items
      if (allResults.length > 0) {
        updateComparisonDataAfterSync(allResults);
      }

      // Show summary
      setTimeout(() => {
        setShowSyncProgress(false);
        setSyncProgressItems([]);
        setItemsNeedingLocation([]);
        setOriginalSyncItems([]); // Clear original items after sync
        setLocationSelections(new Map());
        
        if (successCount > 0 && failureCount === 0) {
          toast.success(`Successfully synced ${successCount} item${successCount !== 1 ? 's' : ''}!`);
        } else if (successCount > 0 && failureCount > 0) {
          toast.success(`Synced ${successCount} item${successCount !== 1 ? 's' : ''}. ${failureCount} failed.`);
        } else {
          toast.error(`Failed to sync all ${failureCount} item${failureCount !== 1 ? 's' : ''}.`);
        }
        
        clearAllSelections();
      }, 2000);

    } catch (error: any) {
      console.error('Sync error:', error);
      setTimeout(() => {
        setShowSyncProgress(false);
        setSyncProgressItems([]);
        setItemsNeedingLocation([]);
        setOriginalSyncItems([]); // Clear original items on error
        setLocationSelections(new Map());
        toast.error('Failed to complete sync operation');
        clearAllSelections();
      }, 2000);
    }
  };

  // Load history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  // Effect to close sync dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (syncDropdownRef.current && !syncDropdownRef.current.contains(event.target as Node)) {
        setShowSyncDropdown(false);
      }
    };

    if (showSyncDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSyncDropdown]);

  // Scroll listener for sticky bar
  useEffect(() => {
    const handleScroll = () => {
      if (actionButtonsRef.current) {
        const rect = actionButtonsRef.current.getBoundingClientRect();
        setShowStickyBar(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Clear selections when tab changes
  useEffect(() => {
    clearAllSelections();
  }, [activeTab]);

  const locationTypes = ['Dc', 'Store', 'Supplier'];

  // Load saved selections from localStorage (vendor is now managed globally by VendorContext)
  useEffect(() => {
    if (user) {
      const savedLocationType = localStorage.getItem(`selectedLocationType_${user.id}`);
      const savedLocation = localStorage.getItem(`selectedLocation_${user.id}`);
      
      if (savedLocationType) setSelectedLocationType(savedLocationType);
      if (savedLocation) setSelectedLocation(savedLocation);
      // Also load cached location name
      const savedLocationName = localStorage.getItem(`selectedLocationName_${user.id}`);
      if (savedLocationName) setCachedLocationName(savedLocationName);
    }
  }, [user]);

  // Load warehouses on user login
  useEffect(() => {
    if (user) {
      loadWarehouses();
    }
  }, [user]);

  // Load ShipHero warehouses
  const loadWarehouses = async () => {
    try {
      console.log('Loading ShipHero warehouses...');
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API_URL}/shiphero/warehouses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Warehouses response:', response.data);
      setWarehouses(response.data.warehouses || []);
      
      // Try to restore previously selected warehouse from localStorage
      if (response.data.warehouses && response.data.warehouses.length > 0) {
        const savedWarehouseId = localStorage.getItem(`selectedWarehouse_shiphero_${user?.id}`);
        const savedWarehouse = savedWarehouseId 
          ? response.data.warehouses.find((w: any) => w.id === savedWarehouseId)
          : null;
        
        if (savedWarehouse) {
          console.log('Restoring saved warehouse:', savedWarehouse);
          setSelectedWarehouse(savedWarehouse);
        } else if (!selectedWarehouse) {
          // Fall back to first warehouse if no saved selection
          const firstWarehouse = response.data.warehouses[0];
          console.log('Auto-selecting warehouse:', firstWarehouse);
          setSelectedWarehouse(firstWarehouse);
        }
      }
    } catch (error: any) {
      console.error('Failed to load warehouses:', error);
      console.error('Error details:', error.response?.data);
      // Check for authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleAuthError();
        return;
      }
      toast.error('Failed to load ShipHero warehouses. Check console for details.');
    }
  };

  // Load locations when vendor AND location type are selected
  useEffect(() => {
    if (selectedVendor && selectedLocationType) {
      loadLocations(selectedVendor);
    } else {
      setLocations([]);
      // Don't clear selectedLocation here - let the onChange handlers manage it
      // This prevents clearing the saved location during initial load
    }
  }, [selectedVendor, selectedLocationType]);

  // Save location name to localStorage and state whenever locations and selectedLocation are available
  useEffect(() => {
    if (user && selectedLocation && locations.length > 0) {
      const selectedLoc = locations.find(l => l.id === selectedLocation);
      if (selectedLoc) {
        const name = selectedLoc.name;
        localStorage.setItem(`selectedLocationName_${user.id}`, name);
        setCachedLocationName(name);
      }
    }
  }, [user, selectedLocation, locations]);

  const loadLocations = async (vendorId: string) => {
    try {
      const token = localStorage.getItem('token');
      console.log('Loading locations for vendor:', vendorId, 'type:', selectedLocationType);
      const response = await axios.get(`${API_URL}/simplerfid/locations/${vendorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Locations response:', response.data);
      const locationsList = response.data.locations || [];
      console.log('Locations list:', locationsList);
      
      // Filter by selected location type
      const filteredLocations = selectedLocationType 
        ? locationsList.filter((loc: Location) => loc.type === selectedLocationType)
        : locationsList;
      console.log('Filtered locations:', filteredLocations);
      
      setLocations(filteredLocations);
      
      // Check if the saved location exists in the filtered list
      if (user && selectedLocation) {
        const selectedLoc = filteredLocations.find((loc: Location) => loc.id === selectedLocation);
        if (selectedLoc) {
          // Save the location name to localStorage for other pages to use
          localStorage.setItem(`selectedLocationName_${user.id}`, selectedLoc.name);
        } else {
          // Clear the selection if the saved location doesn't exist in the current list
          setSelectedLocation('');
          localStorage.removeItem(`selectedLocation_${user.id}`);
          localStorage.removeItem(`selectedLocationName_${user.id}`);
        }
      }
    } catch (error: any) {
      console.error('Error loading locations:', error);
      // Check for authentication errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleAuthError();
        return;
      }
      toast.error(error.response?.data?.error || 'Failed to load locations');
    }
  };

  const handleCompare = async () => {
    if (!selectedLocation) {
      toast.error('Please select a location');
      return;
    }

    setLoading(true);
    setIsComparing(true);
    setLoadingProgress(0);
    progressRef.current = 0;
    try {
      setLoadingStatus('Establishing secure connection to SimpleRFID API servers...');
      animateProgress(15);
      await new Promise(resolve => setTimeout(resolve, 600));
      
      setLoadingStatus('Retrieving inventory data from SimpleRFID database for selected location...');
      animateProgress(15);
      const token = localStorage.getItem('token');
      const selectedLoc = locations.find(l => l.id === selectedLocation);
      await new Promise(resolve => setTimeout(resolve, 600));
      
      setLoadingStatus('Authenticating with ShipHero API using your secure credentials...');
      animateProgress(25);
      await new Promise(resolve => setTimeout(resolve, 600));
      
      if (useSnapshot) {
        setLoadingStatus('Generating ShipHero inventory snapshot (uses fewer API credits)...');
      } else {
        setLoadingStatus('Starting ShipHero data synchronization...');
      }
      animateProgress(30);
      
      // Start the comparison in the background
      const comparePromise = axios.post(`${API_URL}/compare`, {
        vendor_id: selectedVendor,
        location_type: selectedLoc?.type,
        location_id: selectedLocation,
        integration: 'shiphero',
        warehouse_id: selectedWarehouse?.id || '',
        use_snapshot: useSnapshot
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Start polling for progress updates using shared function
      startProgressPolling();
      
      // Wait for the comparison to complete
      const response = await comparePromise;
      
      // Stop polling
      if (progressPollRef.current) {
        clearInterval(progressPollRef.current);
        progressPollRef.current = null;
      }
      
      setLoadingStatus('Categorizing items and preparing comparison results for display...');
      animateProgress(90);
      await new Promise(resolve => setTimeout(resolve, 600));
      
      console.log('Comparison response:', response.data);
      console.log('Matched items:', response.data.matched_items?.length);
      console.log('Unmatched items:', response.data.unmatched_items?.length);
      
      // Log first few items to see SKU format and location data
      if (response.data.unmatched_items && response.data.unmatched_items.length > 0) {
        console.log('First 3 unmatched items:', response.data.unmatched_items.slice(0, 3));
        console.log('First SKU type:', typeof response.data.unmatched_items[0].sku);
        console.log('First SKU value:', response.data.unmatched_items[0].sku);
        console.log('First item location_id:', response.data.unmatched_items[0].location_id);
        console.log('First item LocationID:', response.data.unmatched_items[0].LocationID);
        console.log('First item full object:', JSON.stringify(response.data.unmatched_items[0], null, 2));
      }
      
      setComparisonData(response.data);
      animateProgress(100);
      // Wait for progress to reach 100% before closing
      await new Promise(resolve => setTimeout(resolve, 600));
      toast.success('Inventory comparison completed!');
    } catch (error: any) {
      // Don't show error toast if it was cancelled by user (cancel handler already showed toast)
      const errorMessage = error.response?.data?.error || 'Failed to compare inventory';
      if (!errorMessage.includes('cancelled by user')) {
        toast.error(errorMessage);
      }
    } finally {
      // Clear any remaining timers
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      if (progressPollRef.current) {
        clearInterval(progressPollRef.current);
        progressPollRef.current = null;
      }
      setLoading(false);
      setIsComparing(false);
      setLoadingStatus('');
      setLoadingProgress(0);
      progressRef.current = 0;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header 
        hasUnsavedData={comparisonData !== null}
        locationBadge={{
          simpleRFIDLocationName: locations.find((l: Location) => l.id === selectedLocation)?.name || cachedLocationName || undefined,
          integrationLocationName: selectedWarehouse?.identifier || undefined,
          integrationIcon: '/shiphero_icon.svg',
          onEditLocation: () => {
            if (!isLocationSelectorCollapsed) {
              setLocationContainerGlowing(true);
              setTimeout(() => setLocationContainerGlowing(false), 1000);
            } else {
              setIsLocationSelectorCollapsed(false);
            }
          },
          onRefreshData: (selectedLocation && selectedWarehouse) ? handleCompare : undefined,
          isFlashing: locationBadgeFlashing,
        }}
      />

      {/* Main Content */}
      <main className="w-[99%] min-w-[1060px] px-4 sm:px-6 lg:px-8 pt-12 pb-8">
        {!user?.has_shiphero_key ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <svg className="h-6 w-6 text-yellow-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-yellow-800">
                Please configure your ShipHero credentials to start syncing inventory.
              </p>
            </div>
          </div>
        ) : (
          <>

            {/* Location Selector - Modern Sleek Design */}
            {activeTab !== 'history' && (
              <>
                {!isLocationSelectorCollapsed && (
                  <div className="relative mb-6">
                    {/* Close button - floating square */}
                    <button
                      onClick={() => {
                        setIsLocationSelectorCollapsed(true);
                        setLocationBadgeFlashing(true);
                        setTimeout(() => setLocationBadgeFlashing(false), 1200);
                      }}
                      className="absolute top-3 right-3 w-6 h-6 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors z-10"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <div 
                      className={`bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900 shimmer-animation ${locationContainerGlowing ? 'active' : ''}`}
                    >

                    {/* Two-Column Layout */}
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4 items-start">
                        {/* SimpleRFID Location Card */}
                        <div className="bg-white dark:bg-gray-700/50 rounded-xl p-3 border border-gray-100 dark:border-gray-600/50 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center shrink-0">
                              <img src="/icon.png" alt="SimpleRFID" className="w-6 h-6" />
                            </div>
                            <div className="shrink-0 min-w-[90px]">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">SimpleRFID</span>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Status:</span>
                                <div 
                                  className={`w-2 h-2 rounded-full cursor-help ${selectedLocation ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-500'}`}
                                  title={selectedLocation ? 'Location selected and ready' : 'No location selected'}
                                ></div>
                              </div>
                            </div>
                            <div className="w-px h-10 bg-gray-200 dark:bg-gray-600 mx-1"></div>
                            <div className="flex items-center gap-2 flex-1">
                              <div className="flex-1">
                                <div className="flex items-center gap-1 mb-1">
                                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                  </svg>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Location Type</span>
                                </div>
                                <select
                                  value={selectedLocationType}
                                  onChange={(e) => {
                                    const locationType = e.target.value;
                                    setSelectedLocationType(locationType);
                                    setSelectedLocation('');
                                    if (user) {
                                      localStorage.setItem(`selectedLocationType_${user.id}`, locationType);
                                      localStorage.removeItem(`selectedLocation_${user.id}`);
                                    }
                                  }}
                                  disabled={!selectedVendor}
                                  className="w-full h-8 px-2 text-sm bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                >
                                  <option value="">Select...</option>
                                  {locationTypes.map((type) => (
                                    <option key={type} value={type}>{type}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-1 mb-1">
                                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">Location Name</span>
                                </div>
                                <select
                                  value={selectedLocation}
                                  onChange={(e) => {
                                    const locationId = e.target.value;
                                    setSelectedLocation(locationId);
                                    if (user) {
                                      localStorage.setItem(`selectedLocation_${user.id}`, locationId);
                                      const selectedLoc = locations.find(l => l.id === locationId);
                                      if (selectedLoc) {
                                        localStorage.setItem(`selectedLocationName_${user.id}`, selectedLoc.name);
                                      }
                                    }
                                  }}
                                  disabled={!selectedLocationType || locations.length === 0}
                                  className="w-full h-8 px-2 text-sm bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                >
                                  <option value="">Select...</option>
                                  {locations.map((location) => (
                                    <option key={location.id} value={location.id}>{location.name}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                          {!selectedVendor && (
                            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 ml-11">
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Select vendor first
                            </p>
                          )}
                        </div>

                        {/* Integration Location Card */}
                        <div className="bg-white dark:bg-gray-700/50 rounded-xl p-3 border border-gray-100 dark:border-gray-600/50 shadow-sm">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/50 flex items-center justify-center shrink-0">
                              <img 
                                src="/shiphero_icon.svg" 
                                alt="ShipHero" 
                                className="w-6 h-6" 
                              />
                            </div>
                            <div className="shrink-0 min-w-[90px]">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">ShipHero</span>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Status:</span>
                                <div 
                                  className={`w-2 h-2 rounded-full cursor-help ${selectedWarehouse ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-500'}`}
                                  title={selectedWarehouse ? 'Location selected and ready' : 'No location selected'}
                                ></div>
                              </div>
                            </div>
                            <div className="w-px h-10 bg-gray-200 dark:bg-gray-600 mx-1"></div>
                            <div className="flex-1">
                              <div className="flex items-center gap-1 mb-1">
                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                                <span className="text-xs text-gray-500 dark:text-gray-400">Warehouse</span>
                              </div>
                              <select
                                value={selectedWarehouse?.id || ''}
                                onChange={(e) => {
                                  const warehouse = warehouses.find(w => w.id === e.target.value);
                                  setSelectedWarehouse(warehouse || null);
                                  if (warehouse && user) {
                                    localStorage.setItem(`selectedWarehouse_shiphero_${user.id}`, warehouse.id);
                                  }
                                }}
                                className="w-full h-8 px-2 text-sm bg-gray-50 dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                              >
                                <option value="">Select...</option>
                              {warehouses.map((warehouse) => (
                                <option key={warehouse.id} value={warehouse.id}>
                                  {warehouse.name || warehouse.identifier || warehouse.profile || warehouse.id}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Footer */}
                    <div className="px-4 py-3 bg-gray-50/80 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-700/50 flex items-center justify-center gap-3">
                      {/* Snapshot toggle - only for ShipHero */}
                      {(
                        <div className="flex items-center gap-2" title="Use Inventory Snapshot for lower API credit usage (recommended for large catalogs)">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useSnapshot}
                              onChange={(e) => setUseSnapshot(e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
                          </label>
                          <span className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            Low Credit Mode
                          </span>
                        </div>
                      )}
                      
                      {/* Combined button with status indicator */}
                      <button
                        onClick={() => {
                          handleCompare();
                          setIsLocationSelectorCollapsed(true);
                          setLocationBadgeFlashing(true);
                          setTimeout(() => setLocationBadgeFlashing(false), 1200);
                        }}
                        disabled={loading || !selectedLocation || !selectedWarehouse}
                        className={`group inline-flex rounded-lg overflow-hidden border border-primary-200 dark:border-primary-700 disabled:border-gray-300 dark:disabled:border-gray-600 transition-colors ${!loading && selectedLocation && selectedWarehouse ? 'btn-active-pulse' : ''}`}
                      >
                        <div className="inline-flex items-center gap-1.5 px-4 h-8 bg-primary-50 dark:bg-primary-900/50 group-hover:bg-primary-100 dark:group-hover:bg-primary-900 group-disabled:bg-gray-100 dark:group-disabled:bg-gray-700 text-primary-700 dark:text-primary-300 group-disabled:text-gray-400 text-sm font-medium transition-colors">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          {loading ? 'Comparing...' : 'Compare Inventory'}
                        </div>
                        <div className={`flex items-center gap-1.5 px-3 h-8 border-l transition-colors ${selectedLocation && selectedWarehouse ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700 group-hover:bg-green-100 dark:group-hover:bg-green-900/50' : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 group-hover:bg-gray-100 dark:group-hover:bg-gray-600'}`}>
                          <div className={`w-2 h-2 rounded-full ${selectedLocation && selectedWarehouse ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-500'}`}></div>
                          <span className={`text-xs font-medium ${selectedLocation && selectedWarehouse ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}>
                            {selectedLocation && selectedWarehouse ? 'Ready' : !selectedLocation && !selectedWarehouse ? 'Select locations' : !selectedLocation ? 'Select SimpleRFID location' : 'Select integration location'}
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>
                  </div>
                )}
              </>
            )}

            {/* Stats Summary Section */}
            {comparisonData && (
              <div className="mb-6 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Current Locations */}
                  <div className="bg-white dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Current Locations</p>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <img src="/icon.png" alt="SimpleRFID" className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium text-gray-900 dark:text-white truncate">{cachedLocationName || 'None'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <img src="/shiphero_icon.svg" alt="ShipHero" className="w-3.5 h-3.5" />
                          <span className="text-xs font-medium text-gray-900 dark:text-white truncate">{selectedWarehouse?.identifier || 'None'}</span>
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center ml-2">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Matched with Match Rate */}
                  <div className="bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-gray-700/50 rounded-lg border border-green-200 dark:border-green-800 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">Matched</p>
                        <div className="flex items-baseline gap-2">
                          {(() => {
                            const matched = comparisonData?.matched_count || 0;
                            const inflated = inflatedCount;
                            const deflated = deflatedCount;
                            const total = matched + inflated + deflated;
                            const percentage = total === 0 ? '0' : (matched / total * 100).toFixed(2);
                            return (
                              <>
                                <p className="text-xl font-bold text-green-700 dark:text-green-300">{matched}/{total}</p>
                                <span className="text-xs font-medium text-green-600 dark:text-green-400">({percentage}%)</span>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Inflated (Over in SimpleRFID) */}
                  <div className="bg-gradient-to-br from-red-50 to-white dark:from-red-900/20 dark:to-gray-700/50 rounded-lg border border-red-200 dark:border-red-800 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wider">Inflated</p>
                        <p className="text-xl font-bold text-red-700 dark:text-red-300">{inflatedCount}</p>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Deflated (Under in SimpleRFID) */}
                  <div className="bg-gradient-to-br from-yellow-50 to-white dark:from-yellow-900/20 dark:to-gray-700/50 rounded-lg border border-yellow-200 dark:border-yellow-800 px-3 py-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-yellow-600 dark:text-yellow-400 uppercase tracking-wider">Deflated</p>
                        <p className="text-xl font-bold text-yellow-700 dark:text-yellow-300">{deflatedCount}</p>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center">
                        <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs - always show so user can access history */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-900">
                <div className="border-b dark:border-gray-700">
                  <nav className="flex items-center -mb-px">
                    <button
                      className={`py-4 px-6 border-b-2 font-medium text-sm ${
                        activeTab === 'unmatched'
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                      onClick={() => setActiveTab('unmatched')}
                    >
                      Unmatched
                      <span className="ml-2 inline-flex rounded-full text-xs font-semibold">
                        <span className="bg-red-50 dark:bg-red-900/50 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700 px-2 py-0.5 rounded-l-full">
                          {inflatedCount}
                        </span>
                        <span className="bg-yellow-50 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400 border border-l-0 border-yellow-200 dark:border-yellow-700 px-2 py-0.5 rounded-r-full">
                          {deflatedCount}
                        </span>
                      </span>
                    </button>
                    <button
                      className={`py-4 px-6 border-b-2 font-medium text-sm ${
                        activeTab === 'matched'
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                      onClick={() => setActiveTab('matched')}
                    >
                      Matched
                      <span className="ml-2 bg-green-50 dark:bg-green-900/50 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                        {comparisonData?.matched_count || 0}
                      </span>
                    </button>
                    <button
                      className={`py-4 px-6 border-b-2 font-medium text-sm ${
                        activeTab === 'not_imported'
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                      onClick={() => setActiveTab('not_imported')}
                    >
                      Not Imported
                      <span className="ml-2 bg-blue-50 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                        {comparisonData?.not_imported_count || 0}
                      </span>
                    </button>
                    <button
                      className={`py-4 px-6 border-b-2 font-medium text-sm ${
                        activeTab === 'history'
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                      onClick={() => setActiveTab('history')}
                    >
                      Sync History
                      {syncHistory.length > 0 && (
                        <span className="ml-2 bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                          {syncHistory.length}
                        </span>
                      )}
                    </button>
                    {activeTab !== 'history' && comparisonData && (
                      <div className="text-sm text-gray-500 dark:text-gray-400 pr-6 ml-auto">
                        Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
                      </div>
                    )}
                    {activeTab !== 'history' && !comparisonData && isLocationSelectorCollapsed && selectedLocation && (
                      <div className="ml-auto pr-4">
                        <button
                          onClick={() => {
                            handleCompare();
                            setIsLocationSelectorCollapsed(true);
                          }}
                          disabled={loading}
                          className={`inline-flex items-center gap-1.5 px-4 h-8 bg-primary-50 dark:bg-primary-900/50 hover:bg-primary-100 dark:hover:bg-primary-900 disabled:bg-gray-100 dark:disabled:bg-gray-700 text-primary-700 dark:text-primary-300 disabled:text-gray-400 text-sm rounded font-medium transition-colors border border-primary-200 dark:border-primary-700 disabled:border-gray-300 dark:disabled:border-gray-600 ${!loading ? 'btn-active-pulse' : ''}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          {loading ? 'Comparing...' : 'Compare Inventory'}
                        </button>
                      </div>
                    )}
                  </nav>
                </div>

                {/* Comparison Data Content - only show when not on history tab */}
                {activeTab !== 'history' && (
                <div className="p-2">
                  {!comparisonData ? (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-8">No data to display. {isLocationSelectorCollapsed ? 'Click "Run Comparison" to compare inventory.' : 'Select a location and compare inventory.'}</p>
                  ) : (
                    <>
                      {/* Search Bar and Filter */}
                      <div className="mb-4">
                        <div className="flex gap-3 items-center" ref={actionButtonsRef}>
                          <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                            </div>
                            <input
                              type="text"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              placeholder="Search by SKU or Name..."
                              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            />
                            {searchTerm && (
                              <button
                                onClick={() => setSearchTerm('')}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                              >
                                <svg className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                          
                          {/* Difference Filter - Only show on unmatched tab */}
                          {activeTab === 'unmatched' && (
                            <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 p-1 rounded-lg">
                              <button
                                onClick={() => setDifferenceFilter('all')}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                  differenceFilter === 'all'
                                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                                }`}
                              >
                                All
                              </button>
                              <button
                                onClick={() => setDifferenceFilter('inflated')}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1 ${
                                  differenceFilter === 'inflated'
                                    ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-700 dark:hover:text-red-400'
                                }`}
                              >
                                Inflated in
                                <img src={getIntegrationIcon()} alt={getIntegrationName()} className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDifferenceFilter('deflated')}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1 ${
                                  differenceFilter === 'deflated'
                                    ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:bg-yellow-50 dark:hover:bg-yellow-900/30 hover:text-yellow-700 dark:hover:text-yellow-400'
                                }`}
                              >
                                Deflated in
                                <img src={getIntegrationIcon()} alt={getIntegrationName()} className="w-4 h-4" />
                              </button>
                            </div>
                          )}

                          {/* Column Customizer */}
                          <ColumnCustomizer
                            columns={tableColumns}
                            onColumnsChange={setTableColumns}
                            storageKey="compareSync"
                          />

                          {/* Action Buttons - Print/Sync for unmatched only */}
                          {activeTab === 'unmatched' && (
                            <>
                              <button
                            onClick={handlePrint}
                            disabled={selectedItems.size === 0}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                              selectedItems.size === 0
                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-500 text-white hover:bg-blue-600'
                            }`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            Print {selectedItems.size > 0 && `(${selectedItems.size})`}
                              </button>

                              {/* Sync Dropdown Button */}
                              <div className="relative" ref={syncDropdownRef}>
                                <button
                                  onClick={() => setShowSyncDropdown(!showSyncDropdown)}
                                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors border bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-300 dark:border-green-700 hover:bg-green-100 dark:hover:bg-green-900/50"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  Sync
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>

                                {/* Dropdown Menu */}
                                {showSyncDropdown && (
                                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                                    <button
                                      onClick={handleSyncSelected}
                                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                                    >
                                      <div className="flex-shrink-0">
                                        <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                      </div>
                                      <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Sync Selected</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">Sync items with checked checkboxes ({selectedItems.size})</div>
                                      </div>
                                    </button>
                                    <button
                                      onClick={handleSyncFiltered}
                                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                                    >
                                      <div className="flex-shrink-0">
                                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                        </svg>
                                      </div>
                                      <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Sync All Filtered</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">Sync all items in filtered view ({filteredAndSortedItems.length})</div>
                                      </div>
                                    </button>
                                    <button
                                      onClick={handleSyncEverything}
                                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-3"
                                    >
                                      <div className="flex-shrink-0">
                                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                        </svg>
                                      </div>
                                      <div className="flex-1">
                                        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">Sync Everything</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">Sync all items in this tab ({currentItems.length})</div>
                                      </div>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </>
                          )}

                        </div>
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 mt-2.5">
                          <thead className="bg-gray-100 dark:bg-gray-900">
                            <tr>
                              {activeTab !== 'not_imported' && (
                                <th className="pl-1.5 py-4 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                                  Image
                                </th>
                              )}
                              <th 
                                onClick={() => handleSort('sku')}
                                className={`py-4 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-40 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${activeTab === 'not_imported' ? 'pl-4' : ''}`}
                              >
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                                  </svg>
                                  SKU
                                  {sortColumn === 'sku' && (
                                    <svg className={`w-4 h-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  )}
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('name')}
                                className="py-4 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                  </svg>
                                  Name
                                  {sortColumn === 'name' && (
                                    <svg className={`w-4 h-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  )}
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('color')}
                                className="py-4 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                  </svg>
                                  Color
                                  {sortColumn === 'color' && (
                                    <svg className={`w-4 h-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  )}
                                </div>
                              </th>
                              <th 
                                onClick={() => handleSort('size')}
                                className="py-4 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18M3 12h18M3 18h18" />
                                  </svg>
                                  Size
                                  {sortColumn === 'size' && (
                                    <svg className={`w-4 h-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  )}
                                </div>
                              </th>
                              {activeTab !== 'not_imported' && (
                                <th 
                                  onClick={() => handleSort('location')}
                                  className="py-4 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-40 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                >
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  Location
                                  {sortColumn === 'location' && (
                                    <svg className={`w-4 h-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  )}
                                </div>
                                </th>
                              )}
                              {activeTab !== 'not_imported' && (
                                <th 
                                  onClick={() => handleSort('simplerfid')}
                                  className="py-4 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                >
                                <div className="flex items-center gap-2">
                                  <img src="/icon.png" alt="SimpleRFID" className="w-4 h-4 object-contain" />
                                  <span className="text-xs">SimpleRFID</span>
                                  {sortColumn === 'simplerfid' && (
                                    <svg className={`w-4 h-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  )}
                                </div>
                                </th>
                              )}
                              {activeTab !== 'not_imported' && (
                                <th 
                                  onClick={() => handleSort('shiphero')}
                                  className="py-4 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-36 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                >
                                <div className="flex items-center gap-2 whitespace-nowrap">
                                  <img src={getIntegrationIcon()} alt={getIntegrationName()} className="w-4 h-4 object-contain" />
                                  <span className="text-xs">{getIntegrationName()}</span>
                                  {sortColumn === 'shiphero' && (
                                    <svg className={`w-4 h-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  )}
                                </div>
                                </th>
                              )}
                              {activeTab !== 'matched' && activeTab !== 'not_imported' && (
                                <th 
                                  onClick={() => handleSort('difference')}
                                  className="py-4 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                >
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                  </svg>
                                  Difference
                                  {sortColumn === 'difference' && (
                                    <svg className={`w-4 h-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                  )}
                                </div>
                                </th>
                              )}
                              {activeTab !== 'matched' && (
                                <th className="py-4 text-center w-12">
                                  <input
                                    type="checkbox"
                                    checked={allPageSelected}
                                    onChange={toggleSelectAll}
                                    className="w-4 h-4 mt-1.5 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                                    style={{ accentColor: activeTab === 'not_imported' ? '#9333ea' : '#2563eb' }}
                                  />
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {paginatedItems.map((item: any, index: number) => {
                              const itemSku = String(item.sku || item.SKU);
                              const hasFailed = failedImages.has(itemSku);
                              const imageUrl = item.image_url ? `${BASE_URL}${item.image_url}?token=${localStorage.getItem('token')}` : null;
                              
                              return (
                              <tr key={`${currentPage}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                {activeTab !== 'not_imported' && (
                                  <td className="py-4 cursor-pointer" onClick={() => setSelectedItem(item)}>
                                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden relative">
                                    {imageUrl && !hasFailed ? (
                                        <img 
                                          src={imageUrl}
                                          alt={item.name || item.Name || 'Product'} 
                                          className="w-full h-full object-cover"
                                          loading="lazy"
                                          onError={() => {
                                            setFailedImages((prev: Set<string>) => new Set(prev).add(itemSku));
                                          }}
                                        />
                                    ) : (
                                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                      </svg>
                                    )}
                                  </div>
                                  </td>
                                )}
                                <td className={`py-4 text-sm font-medium text-gray-900 dark:text-gray-100 font-mono cursor-pointer ${activeTab === 'not_imported' ? 'pl-4' : ''}`} onClick={() => setSelectedItem(item)}>{String(item.sku || item.SKU)}</td>
                                <td className="py-4 text-sm text-gray-700 dark:text-gray-300 cursor-pointer" onClick={() => setSelectedItem(item)}>{item.name || item.Name || '-'}</td>
                                <td className="py-4 text-sm text-gray-600 dark:text-gray-400 cursor-pointer" onClick={() => setSelectedItem(item)}>{item.color || item.Color || '-'}</td>
                                <td className="py-4 text-sm text-gray-600 dark:text-gray-400 cursor-pointer" onClick={() => setSelectedItem(item)}>{item.size || item.Size || '-'}</td>
                                {activeTab !== 'not_imported' && (
                                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400 cursor-pointer" onClick={() => setSelectedItem(item)}>
                                    {item.location_name || item.LocationName || item.location_id || item.LocationID ? (
                                      <span className="inline-flex items-center gap-1 text-blue-600">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        {item.location_name || item.LocationName || item.location_id || item.LocationID}
                                      </span>
                                    ) : (
                                      <span className="relative group inline-flex items-center gap-1 text-amber-500 dark:text-amber-400 font-medium">
                                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                        scan-4-me
                                        <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 rounded-lg bg-gray-900 dark:bg-gray-700 px-3 py-2 text-xs font-normal text-gray-100 shadow-lg border border-gray-700 dark:border-gray-600 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 text-center leading-relaxed">
                                          This item has no current location and will be auto-assigned to <span className="font-semibold text-amber-400">scan-4-me</span> on sync
                                          <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></span>
                                        </span>
                                      </span>
                                    )}
                                  </td>
                                )}
                                {activeTab !== 'not_imported' && (
                                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400 text-center font-semibold cursor-pointer" onClick={() => setSelectedItem(item)}>{item.simplerfid_qty ?? item.SimpleRFIDQty ?? 0}</td>
                                )}
                                {activeTab !== 'not_imported' && (
                                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400 text-center font-semibold cursor-pointer" onClick={() => setSelectedItem(item)}>{item.shiphero_qty ?? item.ShipHeroQty ?? 0}</td>
                                )}
                                {activeTab !== 'matched' && activeTab !== 'not_imported' && (
                                  <td className="py-4 text-sm text-center cursor-pointer" onClick={() => setSelectedItem(item)}>
                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                                      item.difference > 0 ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' : 
                                      item.difference < 0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300' : 
                                      'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                                    }`}>
                                      {item.difference > 0 ? '+' : ''}{item.difference ?? item.Difference ?? 0}
                                    </span>
                                  </td>
                                )}
                                {activeTab !== 'matched' && (
                                  <td className="py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      checked={selectedItems.has(itemSku)}
                                      onChange={() => toggleSelectItem(itemSku)}
                                      className="w-4 h-4 mt-1.5 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                                      style={{ accentColor: '#2563eb' }}
                                    />
                                  </td>
                                )}
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {/* Pagination Controls */}
                        {totalPages > 0 && (
                          <div className="bg-white dark:bg-gray-800 flex items-center justify-between border-t border-gray-200 dark:border-gray-700" style={{ paddingTop: '1.3rem', paddingBottom: '0rem' }}>
                            <div className="flex-1 flex items-center">
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Results:
                                </span>
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
                                  {[10, 25, 100, 500].map((count) => (
                                    <button
                                      key={count}
                                      onClick={() => handleItemsPerPageChange(count.toString())}
                                      className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                        itemsPerPage === count
                                          ? 'bg-blue-500 text-white'
                                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                      }`}
                                    >
                                      {count}
                                    </button>
                                  ))}
                                  <button
                                    onClick={() => handleItemsPerPageChange(totalItems.toString())}
                                    className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                                      itemsPerPage === totalItems
                                        ? 'bg-blue-500 text-white'
                                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                                  >
                                    All
                                  </button>
                                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-500 mx-1"></div>
                                  <input
                                    type="number"
                                    min="1"
                                    value={customItemsInput}
                                    onChange={(e) => setCustomItemsInput(e.target.value)}
                                    onBlur={(e) => handleItemsPerPageChange(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handleItemsPerPageChange(customItemsInput);
                                      }
                                    }}
                                    placeholder="Custom"
                                    className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              </div>
                              <div className="flex items-center gap-3 ml-auto">
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                  <button
                                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                    disabled={currentPage === 1}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium ${
                                      currentPage === 1
                                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                  
                                  {/* Page Numbers with Ellipses */}
                                  {(() => {
                                    const pages = [];
                                    const showEllipsis = totalPages > 7;
                                    
                                    if (!showEllipsis) {
                                      // Show all pages if 7 or fewer
                                      for (let i = 1; i <= totalPages; i++) {
                                        pages.push(
                                          <button
                                            key={i}
                                            onClick={() => setCurrentPage(i)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                              currentPage === i
                                                ? 'z-10 bg-blue-50 dark:bg-blue-900/50 border-blue-500 text-blue-600 dark:text-blue-400'
                                                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                            }`}
                                          >
                                            {i}
                                          </button>
                                        );
                                      }
                                    } else {
                                      // Always show first page
                                      pages.push(
                                        <button
                                          key={1}
                                          onClick={() => setCurrentPage(1)}
                                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                            currentPage === 1
                                              ? 'z-10 bg-blue-50 dark:bg-blue-900/50 border-blue-500 text-blue-600 dark:text-blue-400'
                                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                          }`}
                                        >
                                          1
                                        </button>
                                      );
                                      
                                      // Determine pages to show around current page
                                      let startPage = Math.max(2, currentPage - 1);
                                      let endPage = Math.min(totalPages - 1, currentPage + 1);
                                      
                                      // Show ellipsis after first page if needed
                                      if (startPage > 2) {
                                        pages.push(
                                          <span key="ellipsis1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-400">
                                            ...
                                          </span>
                                        );
                                      }
                                      
                                      // Show pages around current page
                                      for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                          <button
                                            key={i}
                                            onClick={() => setCurrentPage(i)}
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                              currentPage === i
                                                ? 'z-10 bg-blue-50 dark:bg-blue-900/50 border-blue-500 text-blue-600 dark:text-blue-400'
                                                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                            }`}
                                          >
                                            {i}
                                          </button>
                                        );
                                      }
                                      
                                      // Show ellipsis before last page if needed
                                      if (endPage < totalPages - 1) {
                                        pages.push(
                                          <span key="ellipsis2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-400">
                                            ...
                                          </span>
                                        );
                                      }
                                      
                                      // Always show last page
                                      pages.push(
                                        <button
                                          key={totalPages}
                                          onClick={() => setCurrentPage(totalPages)}
                                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                            currentPage === totalPages
                                              ? 'z-10 bg-blue-50 dark:bg-blue-900/50 border-blue-500 text-blue-600 dark:text-blue-400'
                                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                          }`}
                                        >
                                          {totalPages}
                                        </button>
                                      );
                                    }
                                    
                                    return pages;
                                  })()}
                                  
                                  <button
                                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                                    disabled={currentPage === totalPages}
                                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium ${
                                      currentPage === totalPages
                                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </button>
                                </nav>
                                
                                {/* Manual Page Entry */}
                                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg">
                                  <input
                                    type="number"
                                    min="1"
                                    max={totalPages}
                                    value={customPageInput}
                                    onChange={(e) => setCustomPageInput(e.target.value)}
                                    onBlur={(e) => handlePageChange(e.target.value)}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        handlePageChange(customPageInput);
                                      }
                                    }}
                                    placeholder="Page"
                                    className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                  <span className="text-sm text-gray-500 dark:text-gray-400">of {totalPages}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
                )}
              </div>

            {/* Sync History Tab Content */}
            {activeTab === 'history' && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold">Sync History</h2>
                  {syncHistory.length > 0 && (
                    <div className="relative history-menu-container">
                      <button
                        onClick={() => setShowHistoryMenu(!showHistoryMenu)}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="History Options"
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                        </svg>
                      </button>
                      {showHistoryMenu && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                          <button
                            onClick={() => {
                              setShowHistoryMenu(false);
                              setShowClearHistoryModal(true);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 rounded-t-lg transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Clear History
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {syncHistory.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <p>No sync history found. Sync some inventory to see it here!</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {syncHistory.map((entry: any) => (
                      <div key={entry.id} className="border rounded-lg overflow-hidden">
                        <div 
                          className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => setExpandedHistoryEntry(expandedHistoryEntry === entry.id ? null : entry.id)}
                        >
                          {/* Expand/Collapse Arrow - Far Left */}
                          <div className="flex-shrink-0">
                            <svg 
                              className={`w-5 h-5 text-gray-600 transition-transform ${expandedHistoryEntry === entry.id ? 'rotate-180' : ''}`} 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>

                          {/* Main Content - Center */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 flex-wrap">
                              <div className="text-sm font-semibold">
                                {new Date(entry.created_at).toLocaleString()}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                                  {entry.success_count} successful
                                </span>
                                {entry.failure_count > 0 && (
                                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">
                                    {entry.failure_count} failed
                                  </span>
                                )}
                                {entry.reverted_at && (
                                  <span className="px-2 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-xs font-medium" title={`Reverted on ${new Date(entry.reverted_at).toLocaleString()}`}>
                                    Reverted on {new Date(entry.reverted_at).toLocaleDateString()} at {new Date(entry.reverted_at).toLocaleTimeString()}
                                  </span>
                                )}
                                {!entry.reverted_at && entry.items && entry.items.every((item: any) => item.reverted_at) && (
                                  <span className="px-2 py-1 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-xs font-medium">
                                    All Items Reverted
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Revert All Button - Far Right */}
                          {!entry.reverted_at && entry.success_count > 0 && !(entry.items && entry.items.every((item: any) => item.reverted_at)) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRevertEntireSync(entry.id);
                              }}
                              className="flex-shrink-0 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                              </svg>
                              Revert All
                            </button>
                          )}
                        </div>
                        
                        {expandedHistoryEntry === entry.id && (
                          <div className="border-t bg-gray-50 px-4 py-3">
                            <div className="space-y-2">
                              {entry.items && entry.items.map((item: any, idx: number) => (
                                <div key={idx} className={`flex items-center gap-4 p-3 rounded ${item.status === 'success' ? 'bg-white border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                                  {/* Main Content - Left and Center */}
                                  <div className="flex-1 flex items-center gap-4">
                                    <div className="min-w-0 flex-1">
                                      <div className="font-medium text-sm">{item.sku}</div>
                                      {item.name && <div className="text-xs text-gray-600 truncate">{item.name}</div>}
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                      {item.reverted_at && (
                                        <span className="px-2 py-0.5 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded text-xs font-medium" title={`Reverted on ${new Date(item.reverted_at).toLocaleString()}`}>
                                          Reverted {new Date(item.reverted_at).toLocaleDateString()} {new Date(item.reverted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                      )}
                                      <div className="text-gray-600">
                                        Before: <span className="font-medium">{item.quantity_before}</span>
                                      </div>
                                      <div>→</div>
                                      <div className="text-gray-600">
                                        After: <span className="font-medium">{item.quantity_after}</span>
                                      </div>
                                      <div className="text-gray-600">
                                        Change: <span className={`font-medium ${item.quantity_delta > 0 ? 'text-green-600' : item.quantity_delta < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                          {item.quantity_delta > 0 && '+'}{item.quantity_delta}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Checkbox - Far Right (hidden if reverted) */}
                                  {!(item.reverted_at || entry.reverted_at || item.status !== 'success') && (
                                    <div className="flex-shrink-0">
                                      <input
                                        type="checkbox"
                                        checked={selectedHistoryItems.has(`${entry.id}|||${item.sku}`)}
                                        onChange={() => {
                                          const key = `${entry.id}|||${item.sku}`;
                                          setSelectedHistoryItems(prev => {
                                            const newSet = new Set(prev);
                                            if (newSet.has(key)) {
                                              newSet.delete(key);
                                            } else {
                                              newSet.add(key);
                                            }
                                            return newSet;
                                          });
                                        }}
                                        className="w-5 h-5 mt-1.5 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                                        style={{ accentColor: '#2563eb' }}
                                      />
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* History Sticky Action Bar */}
      {activeTab === 'history' && selectedHistoryItems.size > 0 && (
        <div className="fixed top-0 left-0 right-0 bg-white shadow-lg border-b-2 border-red-500 z-40 transition-all duration-300">
          <div 
            className="max-w-7xl mx-auto px-4 py-3 cursor-pointer"
            onClick={() => setHistoryStickyBarExpanded(!historyStickyBarExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                    {selectedHistoryItems.size}
                  </div>
                  <span className="font-medium text-gray-900">
                    {selectedHistoryItems.size} {selectedHistoryItems.size === 1 ? 'item' : 'items'} selected for revert
                  </span>
                </div>
                
                <button
                  onClick={(e) => { e.stopPropagation(); handleRevertSelectedItems(); }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg font-medium text-sm hover:bg-red-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Revert Selected
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedHistoryItems(new Set()); }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-300 transition-colors"
                >
                  Clear Selection
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {historyStickyBarExpanded ? 'Click to collapse' : 'Click to expand'}
                </span>
                <svg 
                  className={`w-5 h-5 text-gray-600 transition-transform ${historyStickyBarExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Expanded Details */}
            {historyStickyBarExpanded && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">Selected Items for Revert:</h4>
                <div className="max-h-60 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {getSelectedHistoryItemsData().map((item: any) => (
                      <div key={`${item.historyId}-${item.sku}`} className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{item.sku}</div>
                          <div className="text-xs text-gray-600">{item.name || '-'}</div>
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <span className="text-red-600 font-medium">{item.quantity_after}</span>
                          <span>→</span>
                          <span className="text-green-600 font-medium">{item.quantity_before}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedItem(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center rounded-t-lg">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Product Details</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 font-mono">{String(selectedItem.sku || selectedItem.SKU)}</p>
              </div>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Product Image */}
                <div className="flex flex-col items-center justify-center h-full">
                  {selectedItem.image_url ? (() => {
                    const cachedImageUrl = `${BASE_URL}${selectedItem.image_url}?token=${localStorage.getItem('token')}`;
                    return (
                    <img 
                      src={cachedImageUrl}
                      alt={selectedItem.name || selectedItem.Name || 'Product'} 
                      className="w-full h-full object-cover rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-600"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23f3f4f6" width="400" height="400"/%3E%3Ctext fill="%239ca3af" font-family="Arial" font-size="18" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    );
                  })() : (
                    <div className="w-full h-full bg-gray-100 dark:bg-gray-700 rounded-lg shadow-lg border-2 border-gray-200 dark:border-gray-600 flex items-center justify-center">
                      <svg className="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Right Column - Product Data */}
                <div className="flex flex-col">
                  {/* Product Information */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-sm mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Product Information</h3>
                    <div className="space-y-3">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">SKU</p>
                        <p className="text-sm font-mono font-semibold text-gray-900 dark:text-gray-100">{String(selectedItem.sku || selectedItem.SKU)}</p>
                      </div>
                      
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Product Name</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedItem.name || selectedItem.Name || 'N/A'}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Color</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedItem.color || '-'}</p>
                        </div>
                        
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Size</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedItem.size || '-'}</p>
                        </div>
                      </div>

                      {/* Location/Bin Information */}
                      <div className={`${(selectedItem.location_name || selectedItem.LocationName || selectedItem.location_id || selectedItem.LocationID) ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'} border rounded-lg p-3`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{getIntegrationName()} Location / Bin</p>
                            {(selectedItem.location_name || selectedItem.LocationName || selectedItem.location_id || selectedItem.LocationID) ? (
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {selectedItem.location_name || selectedItem.LocationName || selectedItem.location_id || selectedItem.LocationID}
                              </p>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                <svg className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                </svg>
                                <p className="text-sm font-medium text-amber-600 dark:text-amber-400">scan-4-me</p>
                                <span className="text-xs text-amber-500 dark:text-amber-400/70 ml-1">(auto-assigned)</span>
                              </div>
                            )}
                          </div>
                          {selectedWarehouse && (
                            <button
                              onClick={async () => {
                                const token = localStorage.getItem('token');
                                try {
                                  const locationsResponse = await axios.get(
                                    `${API_URL}/locations?warehouse_id=${selectedWarehouse.id}`,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  setAvailableLocations(locationsResponse.data.locations || []);
                                  setItemsNeedingLocation([selectedItem]);
                                  setShowLocationModal(true);
                                } catch (error) {
                                  toast.error('Failed to load locations');
                                }
                              }}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded transition-colors"
                            >
                              {selectedItem.location_id || selectedItem.LocationID ? 'Change' : 'Set'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quantity Comparison */}
                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Inventory Quantities</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {/* SimpleRFID Quantity */}
                      <div className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/30 dark:to-gray-800 border-2 border-blue-200 dark:border-blue-700 rounded-lg p-4 shadow-sm relative">
                        <img src="/icon.png" alt="SimpleRFID" className="w-6 h-6 object-contain absolute left-4 top-4" />
                        <div className="flex flex-col items-center">
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">SimpleRFID</p>
                          <p className="text-3xl font-bold text-blue-600">{selectedItem.simplerfid_qty ?? selectedItem.SimpleRFIDQty ?? 0}</p>
                        </div>
                      </div>

                      {/* Integration Quantity */}
                      <div className="bg-gradient-to-br from-orange-50 to-white dark:from-orange-900/30 dark:to-gray-800 border-2 border-orange-200 dark:border-orange-700 rounded-lg p-4 shadow-sm relative">
                        <img src={getIntegrationIcon()} alt={getIntegrationName()} className="w-6 h-6 object-contain absolute left-4 top-4" />
                        <div className="flex flex-col items-center">
                          <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">{getIntegrationName()}</p>
                          <p className="text-3xl font-bold text-orange-600">{selectedItem.shiphero_qty ?? selectedItem.ShipHeroQty ?? 0}</p>
                        </div>
                      </div>
                    </div>

                    {/* Difference Indicator */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 font-medium mb-0.5">Status</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedItem.difference > 0 ? `Inflated in ${getIntegrationName()}` : 
                             selectedItem.difference < 0 ? `Deflated in ${getIntegrationName()}` : 
                             'Quantities Match'}
                          </p>
                        </div>
                        <div className={`px-3 py-1.5 rounded-lg text-lg font-bold ${
                          selectedItem.difference > 0 ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300' : 
                          selectedItem.difference < 0 ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300' : 
                          'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                        }`}>
                          {selectedItem.difference > 0 ? '+' : ''}{selectedItem.difference ?? selectedItem.Difference ?? 0}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end rounded-b-lg">
              <button
                onClick={() => setSelectedItem(null)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Action Bar */}
      {showStickyBar && selectedItems.size > 0 && (
        <div className="fixed top-0 left-0 right-0 bg-white dark:bg-gray-800 shadow-lg border-b-2 border-blue-500 z-40 transition-all duration-300">
          <div 
            className="max-w-7xl mx-auto px-4 py-3 cursor-pointer"
            onClick={() => setStickyBarExpanded(!stickyBarExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                    {selectedItems.size}
                  </div>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {selectedItems.size} {selectedItems.size === 1 ? 'item' : 'items'} selected
                  </span>
                </div>
                
                <button
                  onClick={(e) => { e.stopPropagation(); handlePrint(); }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-medium text-sm hover:bg-blue-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); handleSync(); }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg font-medium text-sm hover:bg-green-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sync to {getIntegrationName()}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); clearAllSelections(); }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium text-sm hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                >
                  Clear Selection
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {stickyBarExpanded ? 'Click to collapse' : 'Click to expand'}
                </span>
                <svg 
                  className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${stickyBarExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Expanded Details */}
            {stickyBarExpanded && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Selected Items:</h4>
                <div className="max-h-60 overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {getSelectedItemsData().map((item: any) => (
                      <div key={item.sku || item.SKU} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="flex-1">
                          <div className="font-medium text-sm dark:text-gray-100">{item.sku || item.SKU}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{item.name || item.Name || '-'}</div>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.simplerfid_qty ?? item.SimpleRFIDQty ?? 0} → {item.shiphero_qty ?? item.ShipHeroQty ?? 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}



      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-8 max-w-lg w-full mx-4">
            <div className="flex flex-col items-center">
              {/* Spinner with Icon */}
              <div className="relative w-20 h-20 mb-6">
                <div className="absolute inset-0 border-4 border-primary-200 dark:border-primary-800 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-primary-600 rounded-full border-t-transparent animate-spin"></div>
                {/* Center icon - Data transfer arrows */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg className="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
              </div>
              
              {/* Status Message - Fixed Height Container */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                {loadingStatus.includes('Revert') || loadingStatus.includes('revert') ? 'Reverting Sync' : 
                 loadingStatus.includes('Syncing') || loadingStatus.includes('sync') ? 'Syncing Inventory' : 'Comparing Inventory'}
              </h3>
              <div className="h-16 flex items-center justify-center">
                <p className="text-sm text-gray-600 dark:text-gray-300 text-center leading-relaxed px-2">
                  {loadingStatus || 'Initializing process...'}
                </p>
              </div>
              
              {/* Animated Progress Bar - 3D Style */}
              <div className="w-full mt-4">
                <div className="relative h-6 bg-gradient-to-b from-gray-300 to-gray-200 dark:from-gray-600 dark:to-gray-700 rounded-full overflow-hidden shadow-lg border border-gray-400 dark:border-gray-500">
                  {/* Inner shadow for depth */}
                  <div className="absolute inset-0 shadow-inner rounded-full"></div>
                  {/* Progress fill with left-to-right pulse */}
                  <div className="relative h-full bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 rounded-full transition-all duration-100 ease-linear animate-progress-pulse shadow-md" 
                       style={{ 
                         width: `${loadingProgress}%`,
                         backgroundSize: '200% 100%'
                       }}>
                    {/* Glossy highlight effect */}
                    <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-transparent rounded-full"></div>
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Progress</span>
                  <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">{loadingProgress}%</span>
                </div>
              </div>
              
              {/* Cancel Button for Comparison */}
              {isComparing && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={handleCancelCompare}
                    className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                  >
                    Cancel Comparison
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Location Selection Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Select Locations for Products</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                These products have 0 quantity in {getIntegrationName()} and need a location assigned before syncing.
              </p>
            </div>

            <div className="p-6 overflow-y-auto max-h-[50vh]">
              <div className="space-y-4">
                {itemsNeedingLocation.map((item: any) => {
                  const sku = item.sku || item.SKU;
                  const name = item.name || item.Name;
                  const qty = item.quantity_after || item.QuantityAfter || 1;
                  const currentLocation = item.location_name || item.LocationName || item.location_id || item.LocationID;
                  
                  // Get locations selected by OTHER items in this batch
                  const otherSelections = Array.from(locationSelections.entries())
                    .filter(([selectedSku, _]: [string, string]) => selectedSku !== sku)
                    .map(([_, locationId]: [string, string]) => locationId);
                  
                  // Filter available locations to exclude those selected by other items
                  const filteredLocations = availableLocations.filter(
                    (loc: any) => !otherSelections.includes(loc.id)
                  );
                  
                  return (
                  <div key={sku} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">{sku}</div>
                        {name && <div className="text-sm text-gray-600 dark:text-gray-400">{name}</div>}
                        {currentLocation && (
                          <div className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Location: {currentLocation}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Qty: {qty}
                      </div>
                    </div>

                    <LocationSearchInput
                      locations={filteredLocations}
                      selectedLocationId={locationSelections.get(sku) || null}
                      onSelect={(locationId) => handleLocationSelect(sku, locationId)}
                    />
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowLocationModal(false);
                  setItemsNeedingLocation([]);
                  setOriginalSyncItems([]); // Clear original items when cancelled
                  setLocationSelections(new Map());
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Check if this is a single-item location update (from details modal) or multi-item sync
                  if (itemsNeedingLocation.length === 1 && selectedItem && 
                      (itemsNeedingLocation[0].sku || itemsNeedingLocation[0].SKU) === (selectedItem.sku || selectedItem.SKU)) {
                    // Single item from details modal - just save location
                    const locationId = locationSelections.get(itemsNeedingLocation[0].sku || itemsNeedingLocation[0].SKU);
                    if (locationId) {
                      handleSaveSingleLocation(itemsNeedingLocation[0], locationId);
                    }
                  } else {
                    // Multi-item sync operation
                    handleSaveLocationsAndSync();
                  }
                }}
                disabled={itemsNeedingLocation.some((item: any) => !locationSelections.get(item.sku || item.SKU))}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {itemsNeedingLocation.length === 1 && selectedItem && 
                 (itemsNeedingLocation[0].sku || itemsNeedingLocation[0].SKU) === (selectedItem.sku || selectedItem.SKU) 
                  ? 'Save Location' 
                  : 'Save & Sync'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Progress Modal */}
      {showSyncProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Syncing Items to {getIntegrationName()}</h2>
              
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {syncProgressItems.map((item, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{item.sku}</span>
                          {item.status === 'pending' && (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded text-xs">Waiting...</span>
                          )}
                          {item.status === 'saving' && (
                            <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded text-xs flex items-center gap-1">
                              <div className="w-3 h-3 border-2 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
                              Saving...
                            </span>
                          )}
                          {item.status === 'syncing' && (
                            <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 rounded text-xs flex items-center gap-1">
                              <div className="w-3 h-3 border-2 border-yellow-700 border-t-transparent rounded-full animate-spin"></div>
                              Syncing...
                            </span>
                          )}
                          {item.status === 'success' && (
                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded text-xs flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              Success
                            </span>
                          )}
                          {item.status === 'error' && (
                            <span className="px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded text-xs flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                              Failed
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{item.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.message}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex justify-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {syncProgressItems.filter(i => i.status === 'success').length} of {syncProgressItems.length} completed
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear History Confirmation Modal */}
      {showClearHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Clear All History?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    This will permanently delete all sync history entries. This action cannot be undone.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 justify-end mt-6">
                <button
                  onClick={() => setShowClearHistoryModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearHistory}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium"
                >
                  Clear All History
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal — fully isolated, manages its own state */}
      <PrintModal
        show={showPrintModal}
        items={itemsToPrint}
        vendorId={selectedVendor}
        apiUrl={API_URL}
        onClose={() => { setShowPrintModal(false); setItemsToPrint([]); }}
      />

      {/* Revert Selected Items Confirmation Modal */}
      {showRevertModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Revert Selected Items?</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    You are about to revert {selectedHistoryItems.size} item{selectedHistoryItems.size !== 1 ? 's' : ''} from your sync history. This will restore the previous quantities in {getIntegrationName()}.
                  </p>
                </div>
              </div>
              
              {/* Summary of items */}
              <div className="mt-4 mb-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 max-h-60 overflow-y-auto">
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Items to Revert:</h4>
                <div className="space-y-2">
                  {getSelectedHistoryItemsData().map((item: any, idx: number) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600">
                      <div className="flex-1">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{item.sku}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2">{item.name}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-gray-600 dark:text-gray-400">
                          Qty: <span className={item.quantity_delta > 0 ? 'text-green-600 dark:text-green-400 font-medium' : item.quantity_delta < 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-600 dark:text-gray-400'}>
                            {item.quantity_delta > 0 && '+'}{item.quantity_delta}
                          </span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowRevertModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRevertSelected}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Confirm Revert
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default CompareSync;
